/**
 * macOS Installation Guide Page
 * Completely synced with docs/tutorials/macos-install.html
 */

import { useState, useCallback } from 'react';
import { ArrowLeft, ExternalLink, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useI18n, useLocalizedPath } from '@/lib/i18n';
import { Lightbox } from '@/components/docs/Lightbox';
import './docs.scss';

// Types for the macOS install data structure
interface Subsection {
  title: string;
  content: string;
}

interface Step {
  title: string;
  content: string;
  subsections?: Subsection[];
  warning?: string;
  info?: string;
  success?: string;
}

interface TroubleshootingIssue {
  title: string;
  content: string;
}

interface MacOSInstallData {
  pageTitle: string;
  langLabel: string;
  backLink: string;
  tocTitle: string;
  mobileJumpTo: string;
  overview: string;
  requirements: {
    title: string;
    items: string[];
  };
  steps: Step[];
  troubleshooting: {
    title: string;
    issues: TroubleshootingIssue[];
  };
}

// Translations object - exactly matching macos-install.html
const translations: Record<string, MacOSInstallData> = {
  en: {
    pageTitle: "macOS Installation Guide",
    langLabel: "Language:",
    backLink: "← Back to Documentation",
    tocTitle: "Table of Contents",
    mobileJumpTo: "Jump to section...",
    overview: "This guide will walk you through installing Sokuji on macOS systems. Sokuji is available as a signed and notarized .pkg installer that includes a virtual audio driver for enhanced audio processing capabilities.",
    requirements: {
      title: "System Requirements",
      items: [
        "macOS 13 (Ventura) or later (since v0.42.2; on macOS 12, keep using v0.42.1)",
        "Apple Silicon (M1/M2/M3/M4) or Intel (x86_64) processor",
        "4GB RAM minimum (8GB recommended)",
        "200MB available disk space",
        "Administrator privileges (required for virtual audio driver installation)",
        "Internet connection for AI translation services",
        "Microphone and speakers/headphones"
      ]
    },
    steps: [
      {
        title: "Step 1: Download Sokuji",
        content: `Visit the <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">official GitHub releases page</a> to download the latest version of Sokuji for macOS.`,
        subsections: [
          {
            title: "Download the PKG Installer",
            content: `<ul>
              <li><strong>Sokuji-x.x.x-arm64.pkg</strong> - For Apple Silicon Macs (M1/M2/M3/M4)</li>
              <li><strong>Sokuji-x.x.x-x64.pkg</strong> - For Intel Macs</li>
              <li>Each installer includes the Sokuji app and the virtual audio driver</li>
              <li>File size is approximately 150-200MB</li>
            </ul>
            <p><strong>Tip:</strong> Not sure which one to download? Click the Apple menu () → "About This Mac" to check your chip type. If you see "Apple M1/M2/M3/M4", choose the arm64 version. If you see "Intel", choose the x64 version.</p>`
          }
        ],
        info: "Since version 0.46.5 the PKG installer is signed and notarized, so macOS opens it without any extra steps."
      },
      {
        title: "Step 2: Install PKG",
        content: `Install Sokuji and its virtual audio driver.`,
        subsections: [
          {
            title: "Installation Process",
            content: `<ol>
              <li>Locate the downloaded <code>.pkg</code> file in your Downloads folder (e.g., <code>Sokuji-x.x.x-arm64.pkg</code> or <code>Sokuji-x.x.x-x64.pkg</code>)</li>
              <li>Double-click the PKG file to start the installation</li>
              <li>The macOS Installer will open - click "Continue" to proceed</li>
              <li>Review the installation details and click "Install"</li>
              <li>Enter your administrator password when prompted (required for driver installation)</li>
              <li>Wait for the installation to complete (usually 1-2 minutes)</li>
              <li>Click "Close" when installation is finished</li>
            </ol>`
          },
          {
            title: "What Gets Installed",
            content: `<ul>
              <li><strong>Sokuji.app</strong> - Installed to /Applications</li>
              <li><strong>SokujiVirtualAudio.driver</strong> - Virtual audio driver installed to /Library/Audio/Plug-Ins/HAL</li>
              <li>The installer will automatically restart CoreAudio to load the driver</li>
            </ul>`
          }
        ],
        info: "Administrator password is required because the installer needs to install a system-level audio driver. The virtual audio driver enables advanced audio processing features."
      },
      {
        title: "Step 3: First Launch",
        content: `Open Sokuji from your Applications folder. No security workaround is needed.`
      },
      {
        title: "Step 4: Configure Sokuji",
        content: `Now that Sokuji is running, let's configure it for first use:`,
        subsections: [
          {
            title: "Initial Setup",
            content: `<ol>
              <li>Grant microphone permissions when macOS prompts you</li>
              <li>Select your preferred interface language</li>
              <li>Choose your AI provider (OpenAI, Google Gemini, etc.)</li>
              <li>Enter your API key for the selected provider</li>
              <li>Select source and target languages for translation</li>
              <li>Select your microphone, and in the <strong>Speech</strong> block choose your speakers or headphones as the <strong>Default playback device</strong>. Sokuji does not list its own virtual audio device there: <strong>Translation the other side hears</strong> sends your translation into it, as the virtual microphone your meeting app picks</li>
              <li>Test your microphone and speaker setup</li>
            </ol>`
          }
        ],
        success: "Congratulations! Sokuji is now ready to use. The virtual audio driver has been installed and is available in your audio device list. Click the microphone button to start real-time translation."
      }
    ],
    troubleshooting: {
      title: "Troubleshooting",
      issues: [
        {
          title: "Microphone not working",
          content: `If Sokuji can't access your microphone:
          <ol>
            <li>Open System Preferences → Security & Privacy → Privacy</li>
            <li>Select Microphone from the left sidebar</li>
            <li>Make sure Sokuji is checked in the list</li>
            <li>If it's not listed, restart Sokuji and grant permission when prompted</li>
          </ol>`
        },
        {
          title: "No audio output",
          content: `If you can't hear the translated audio:
          <ol>
            <li>Open System Preferences → Sound → Output</li>
            <li>Select your preferred output device</li>
            <li>Check the output volume is not muted</li>
            <li>In Sokuji's <strong>Speech</strong> block, check the device of the row you expect to hear: <strong>I hear it too</strong> for your own translation (off by default), <strong>Translation I hear</strong> for the other side's, and the <strong>Default playback device</strong> for everything else</li>
          </ol>`
        },
        {
          title: "App crashes on Intel Macs or performance issues",
          content: `Make sure you downloaded the correct installer for your Mac:
          <ul>
            <li><strong>Apple Silicon</strong> (M1/M2/M3/M4): Use <code>Sokuji-x.x.x-arm64.pkg</code></li>
            <li><strong>Intel</strong>: Use <code>Sokuji-x.x.x-x64.pkg</code></li>
            <li>Check your chip type: Apple menu () → "About This Mac"</li>
            <li>If you used the wrong installer, download the correct version from the <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">releases page</a></li>
            <li>If crashes persist with the correct installer, check Console.app for crash logs and report them on GitHub</li>
          </ul>`
        },
        {
          title: "Virtual audio driver not showing up",
          content: `If you don't see "SokujiVirtualAudio" in your audio devices:
          <ol>
            <li>Let Sokuji repair it first: when the driver is installed but macOS has not loaded it, Sokuji says so at startup and offers <strong>Repair</strong>. Enter your administrator password and Sokuji restarts Core Audio to load the driver, with no restart of Sokuji needed</li>
            <li>Check if the driver is installed: <code>ls /Library/Audio/Plug-Ins/HAL/</code></li>
            <li>If not present, you may need to reinstall Sokuji</li>
            <li>If present and Repair did not help, try restarting CoreAudio: <code>sudo killall coreaudiod</code>, or restart the Mac</li>
            <li>Check System Settings → Sound to see if the device appears</li>
          </ol>`
        },
        {
          title: "Installation requires administrator password",
          content: `This is normal and required:
          <ul>
            <li>The PKG installer needs to install a system-level audio driver</li>
            <li>System audio drivers require administrator privileges to install</li>
            <li>Enter your password when prompted - this is safe and expected</li>
            <li>If you don't have administrator access, contact your system administrator</li>
          </ul>`
        }
      ]
    },
  },
  zh: {
    pageTitle: "macOS 安装指南",
    langLabel: "语言：",
    backLink: "← 返回文档",
    tocTitle: "目录",
    mobileJumpTo: "跳转到章节...",
    overview: "本指南将指导您在 macOS 系统上安装 Sokuji。Sokuji 以已签名并通过 Apple 公证的 .pkg 安装包形式提供，其中包含用于增强音频处理功能的虚拟音频驱动程序。",
    requirements: {
      title: "系统要求",
      items: [
        "macOS 13 (Ventura) 或更高版本（v0.42.2 起；macOS 12 可继续使用 v0.42.1）",
        "Apple Silicon (M1/M2/M3/M4) 或 Intel (x86_64) 处理器",
        "最低4GB内存（推荐8GB）",
        "200MB可用磁盘空间",
        "管理员权限（安装虚拟音频驱动程序所需）",
        "用于AI翻译服务的互联网连接",
        "麦克风和扬声器/耳机"
      ]
    },
    steps: [
      {
        title: "步骤 1：下载 Sokuji",
        content: `访问 <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">官方 GitHub 发布页面</a> 下载最新的 macOS 版本 Sokuji。`,
        subsections: [
          {
            title: "下载 PKG 安装包",
            content: `<ul>
              <li><strong>Sokuji-x.x.x-arm64.pkg</strong> - 适用于 Apple Silicon Mac (M1/M2/M3/M4)</li>
              <li><strong>Sokuji-x.x.x-x64.pkg</strong> - 适用于 Intel Mac</li>
              <li>每个安装包均包含 Sokuji 应用程序和虚拟音频驱动程序</li>
              <li>文件大小约为 150-200MB</li>
            </ul>
            <p><strong>提示：</strong>不确定下载哪个版本？点击苹果菜单（）→"关于本机"查看芯片类型。如果显示"Apple M1/M2/M3/M4"，请选择 arm64 版本。如果显示"Intel"，请选择 x64 版本。</p>`
          }
        ],
        info: "从 0.46.5 版本起，PKG 安装包已签名并通过公证，macOS 可以直接打开，无需额外步骤。"
      },
      {
        title: "步骤 2：安装 PKG",
        content: `安装 Sokuji 及其虚拟音频驱动程序。`,
        subsections: [
          {
            title: "安装过程",
            content: `<ol>
              <li>在下载文件夹中找到已下载的 <code>.pkg</code> 文件（如 <code>Sokuji-x.x.x-arm64.pkg</code> 或 <code>Sokuji-x.x.x-x64.pkg</code>）</li>
              <li>双击 PKG 文件开始安装</li>
              <li>macOS 安装器将打开 - 点击"继续"继续</li>
              <li>查看安装详情并点击"安装"</li>
              <li>在提示时输入管理员密码（安装驱动程序需要）</li>
              <li>等待安装完成（通常需要 1-2 分钟）</li>
              <li>安装完成后点击"关闭"</li>
            </ol>`
          },
          {
            title: "安装内容",
            content: `<ul>
              <li><strong>Sokuji.app</strong> - 安装到 /Applications</li>
              <li><strong>SokujiVirtualAudio.driver</strong> - 虚拟音频驱动程序安装到 /Library/Audio/Plug-Ins/HAL</li>
              <li>安装器会自动重启 CoreAudio 以加载驱动程序</li>
            </ul>`
          }
        ],
        info: "需要管理员密码是因为安装器需要安装系统级音频驱动程序。虚拟音频驱动程序实现了高级音频处理功能。"
      },
      {
        title: "步骤 3：首次启动",
        content: `从"应用程序"文件夹打开 Sokuji，无需任何安全绕过步骤。`
      },
      {
        title: "步骤 4：配置 Sokuji",
        content: `现在 Sokuji 正在运行，让我们进行首次使用配置：`,
        subsections: [
          {
            title: "初始设置",
            content: `<ol>
              <li>当 macOS 提示时授予麦克风权限</li>
              <li>选择您喜欢的界面语言</li>
              <li>选择您的 AI 提供商（OpenAI、Google Gemini 等）</li>
              <li>输入所选提供商的 API 密钥</li>
              <li>选择翻译的源语言和目标语言</li>
              <li>选择您的麦克风，并在<strong>语音</strong>区块中把<strong>默认播放设备</strong>设为您的扬声器或耳机。Sokuji 不会在这里列出它自己的虚拟音频设备：<strong>对方听到的翻译</strong>会把您的翻译送进这个设备，也就是会议应用里要选择的虚拟麦克风</li>
              <li>测试您的麦克风和扬声器设置</li>
            </ol>`
          }
        ],
        success: "恭喜！Sokuji 现在已准备就绪。虚拟音频驱动程序已安装并可在您的音频设备列表中使用。点击麦克风按钮开始实时翻译。"
      }
    ],
    troubleshooting: {
      title: "故障排除",
      issues: [
        {
          title: "麦克风不工作",
          content: `如果 Sokuji 无法访问您的麦克风：
          <ol>
            <li>打开系统偏好设置 → 安全性与隐私 → 隐私</li>
            <li>从左侧边栏选择麦克风</li>
            <li>确保列表中的 Sokuji 已勾选</li>
            <li>如果未列出，请重启 Sokuji 并在提示时授予权限</li>
          </ol>`
        },
        {
          title: "无音频输出",
          content: `如果您听不到翻译的音频：
          <ol>
            <li>打开系统偏好设置 → 声音 → 输出</li>
            <li>选择您的首选输出设备</li>
            <li>检查输出音量是否未静音</li>
            <li>在 Sokuji 的<strong>语音</strong>区块中，检查您想听到的那一行所选的设备：您自己的翻译看<strong>我也听</strong>（默认关闭），对方的翻译看<strong>我听到的翻译</strong>，其他声音看<strong>默认播放设备</strong></li>
          </ol>`
        },
        {
          title: "在 Intel Mac 上崩溃或性能问题",
          content: `请确认您下载了正确的安装包：
          <ul>
            <li><strong>Apple Silicon</strong> (M1/M2/M3/M4)：使用 <code>Sokuji-x.x.x-arm64.pkg</code></li>
            <li><strong>Intel</strong>：使用 <code>Sokuji-x.x.x-x64.pkg</code></li>
            <li>查看芯片类型：苹果菜单（）→"关于本机"</li>
            <li>如果使用了错误的安装包，请从<a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">发布页面</a>下载正确的版本</li>
            <li>如果使用正确的安装包仍然崩溃，请检查控制台应用中的崩溃日志并在 GitHub 上报告</li>
          </ul>`
        },
        {
          title: "虚拟音频驱动程序未显示",
          content: `如果您在音频设备中看不到"SokujiVirtualAudio"：
          <ol>
            <li>先让 Sokuji 自己修：驱动已安装但 macOS 没有加载时，Sokuji 启动后会提示“虚拟麦克风已安装，但 macOS 没有加载它”，点击<strong>修复</strong>，输入管理员密码，Sokuji 会重启 Core Audio 加载驱动，无需重启 Sokuji</li>
            <li>检查驱动程序是否已安装：<code>ls /Library/Audio/Plug-Ins/HAL/</code></li>
            <li>如果不存在，您可能需要重新安装 Sokuji</li>
            <li>如果存在而修复无效，尝试重启 CoreAudio：<code>sudo killall coreaudiod</code>，或重启 Mac</li>
            <li>检查系统设置 → 声音以查看设备是否出现</li>
          </ol>`
        },
        {
          title: "安装需要管理员密码",
          content: `这是正常且必需的：
          <ul>
            <li>PKG 安装器需要安装系统级音频驱动程序</li>
            <li>系统音频驱动程序需要管理员权限才能安装</li>
            <li>在提示时输入密码 - 这是安全且预期的</li>
            <li>如果您没有管理员访问权限，请联系系统管理员</li>
          </ul>`
        }
      ]
    },
  },
  ja: {
    pageTitle: "macOS インストールガイド",
    langLabel: "言語：",
    backLink: "← ドキュメントに戻る",
    tocTitle: "目次",
    mobileJumpTo: "セクションにジャンプ...",
    overview: "このガイドでは、macOS システムに Sokuji をインストールする方法を説明します。Sokuji は署名済みで Apple の公証を受けた .pkg インストーラーとして提供され、高度なオーディオ処理機能のための仮想オーディオドライバーが含まれています。",
    requirements: {
      title: "システム要件",
      items: [
        "macOS 13 (Ventura) 以降（v0.42.2 以降。macOS 12 では v0.42.1 を引き続きお使いください）",
        "Apple Silicon (M1/M2/M3/M4) または Intel (x86_64) プロセッサ",
        "最小4GBのRAM（8GB推奨）",
        "200MBの空きディスク容量",
        "管理者権限（仮想オーディオドライバーのインストールに必要）",
        "AI翻訳サービス用のインターネット接続",
        "マイクとスピーカー/ヘッドフォン"
      ]
    },
    steps: [
      {
        title: "ステップ 1：Sokuji をダウンロード",
        content: `<a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">公式 GitHub リリースページ</a>にアクセスして、macOS 用の Sokuji の最新バージョンをダウンロードします。`,
        subsections: [
          {
            title: "PKG インストーラーをダウンロード",
            content: `<ul>
              <li><strong>Sokuji-x.x.x-arm64.pkg</strong> - Apple Silicon Mac (M1/M2/M3/M4) 用</li>
              <li><strong>Sokuji-x.x.x-x64.pkg</strong> - Intel Mac 用</li>
              <li>各インストーラーには Sokuji アプリと仮想オーディオドライバーが含まれています</li>
              <li>ファイルサイズは約 150-200MB です</li>
            </ul>
            <p><strong>ヒント：</strong>どちらをダウンロードすべきかわからない場合は、Apple メニュー（）→「この Mac について」でチップの種類を確認してください。「Apple M1/M2/M3/M4」と表示される場合は arm64 版を、「Intel」と表示される場合は x64 版を選択してください。</p>`
          }
        ],
        info: "バージョン 0.46.5 以降、PKG インストーラーは署名と公証を受けているため、macOS は追加の手順なしで開けます。"
      },
      {
        title: "ステップ 2：PKG をインストール",
        content: `Sokuji とその仮想オーディオドライバーをインストールします。`,
        subsections: [
          {
            title: "インストールプロセス",
            content: `<ol>
              <li>ダウンロードフォルダーでダウンロードした <code>.pkg</code> ファイルを見つけます（例：<code>Sokuji-x.x.x-arm64.pkg</code> または <code>Sokuji-x.x.x-x64.pkg</code>）</li>
              <li>PKG ファイルをダブルクリックしてインストールを開始します</li>
              <li>macOS インストーラーが開きます - 「続ける」をクリックして進みます</li>
              <li>インストールの詳細を確認し、「インストール」をクリックします</li>
              <li>プロンプトが表示されたら管理者パスワードを入力します（ドライバーのインストールに必要）</li>
              <li>インストールが完了するまで待ちます（通常 1〜2 分）</li>
              <li>インストールが完了したら「閉じる」をクリックします</li>
            </ol>`
          },
          {
            title: "インストールされるもの",
            content: `<ul>
              <li><strong>Sokuji.app</strong> - /Applications にインストールされます</li>
              <li><strong>SokujiVirtualAudio.driver</strong> - /Library/Audio/Plug-Ins/HAL にインストールされる仮想オーディオドライバー</li>
              <li>インストーラーは自動的に CoreAudio を再起動してドライバーをロードします</li>
            </ul>`
          }
        ],
        info: "管理者パスワードが必要なのは、インストーラーがシステムレベルのオーディオドライバーをインストールする必要があるためです。仮想オーディオドライバーは高度なオーディオ処理機能を有効にします。"
      },
      {
        title: "ステップ 3：初回起動",
        content: `アプリケーションフォルダから Sokuji を開きます。セキュリティの回避手順は必要ありません。`
      },
      {
        title: "ステップ 4：Sokuji を設定",
        content: `Sokuji が実行されているので、初回使用の設定を行いましょう：`,
        subsections: [
          {
            title: "初期設定",
            content: `<ol>
              <li>macOS がプロンプトを表示したらマイクの権限を付与します</li>
              <li>希望のインターフェース言語を選択します</li>
              <li>AI プロバイダー（OpenAI、Google Gemini など）を選択します</li>
              <li>選択したプロバイダーの API キーを入力します</li>
              <li>翻訳のソース言語とターゲット言語を選択します</li>
              <li>マイクを選択し、<strong>音声</strong>ブロックで<strong>既定の再生デバイス</strong>にスピーカーまたはヘッドフォンを選びます。Sokuji 自身の仮想オーディオデバイスはここには表示されません。<strong>相手に聞こえる翻訳</strong>が翻訳をそのデバイス、つまり会議アプリで選ぶ仮想マイクに送ります</li>
              <li>マイクとスピーカーの設定をテストします</li>
            </ol>`
          }
        ],
        success: "おめでとうございます！Sokuji の準備が整いました。仮想オーディオドライバーがインストールされ、オーディオデバイスリストで利用できます。マイクボタンをクリックしてリアルタイム翻訳を開始してください。"
      }
    ],
    troubleshooting: {
      title: "トラブルシューティング",
      issues: [
        {
          title: "マイクが動作しない",
          content: `Sokuji がマイクにアクセスできない場合：
          <ol>
            <li>システム環境設定 → セキュリティとプライバシー → プライバシー を開きます</li>
            <li>左側のサイドバーからマイクを選択します</li>
            <li>リストで Sokuji がチェックされていることを確認します</li>
            <li>リストにない場合は、Sokuji を再起動し、プロンプトが表示されたら権限を付与します</li>
          </ol>`
        },
        {
          title: "オーディオ出力がない",
          content: `翻訳された音声が聞こえない場合：
          <ol>
            <li>システム環境設定 → サウンド → 出力 を開きます</li>
            <li>希望の出力デバイスを選択します</li>
            <li>出力音量がミュートされていないことを確認します</li>
            <li>Sokuji の<strong>音声</strong>ブロックで、聞きたい行のデバイスを確認します：自分の翻訳は<strong>自分にも再生</strong>（既定ではオフ）、相手の翻訳は<strong>自分に聞こえる翻訳</strong>、それ以外の音は<strong>既定の再生デバイス</strong></li>
          </ol>`
        },
        {
          title: "Intel Mac でアプリがクラッシュする、またはパフォーマンスの問題",
          content: `お使いの Mac に合った正しいインストーラーをダウンロードしたか確認してください：
          <ul>
            <li><strong>Apple Silicon</strong> (M1/M2/M3/M4)：<code>Sokuji-x.x.x-arm64.pkg</code> を使用</li>
            <li><strong>Intel</strong>：<code>Sokuji-x.x.x-x64.pkg</code> を使用</li>
            <li>チップの種類を確認：Apple メニュー（）→「この Mac について」</li>
            <li>間違ったインストーラーを使用した場合は、<a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">リリースページ</a>から正しいバージョンをダウンロードしてください</li>
            <li>正しいインストーラーでもクラッシュが続く場合は、コンソール.app でクラッシュログを確認し、GitHub で報告してください</li>
          </ul>`
        },
        {
          title: "仮想オーディオドライバーが表示されない",
          content: `オーディオデバイスに「SokujiVirtualAudio」が表示されない場合：
          <ol>
            <li>まず Sokuji に修復させます：ドライバーはインストールされているのに macOS に読み込まれていない場合、Sokuji は起動時にその旨を知らせて<strong>修復</strong>を提案します。管理者パスワードを入力すると、Sokuji が Core Audio を再起動してドライバーを読み込みます。Sokuji の再起動は不要です</li>
            <li>ドライバーがインストールされているか確認します：<code>ls /Library/Audio/Plug-Ins/HAL/</code></li>
            <li>存在しない場合は、Sokuji を再インストールする必要がある可能性があります</li>
            <li>存在していて修復でも直らない場合は、CoreAudio を再起動してみてください：<code>sudo killall coreaudiod</code>。または Mac を再起動します</li>
            <li>システム設定 → サウンド でデバイスが表示されるか確認します</li>
          </ol>`
        },
        {
          title: "インストールに管理者パスワードが必要",
          content: `これは正常で必要です：
          <ul>
            <li>PKG インストーラーはシステムレベルのオーディオドライバーをインストールする必要があります</li>
            <li>システムオーディオドライバーのインストールには管理者権限が必要です</li>
            <li>プロンプトが表示されたらパスワードを入力してください - これは安全で予想される動作です</li>
            <li>管理者アクセス権がない場合は、システム管理者に連絡してください</li>
          </ul>`
        }
      ]
    },
  },
  ko: {
    pageTitle: "macOS 설치 가이드",
    langLabel: "언어:",
    backLink: "← 문서로 돌아가기",
    tocTitle: "목차",
    mobileJumpTo: "섹션으로 이동...",
    overview: "이 가이드는 macOS 시스템에 Sokuji를 설치하는 방법을 안내합니다. Sokuji는 향상된 오디오 처리 기능을 위한 가상 오디오 드라이버가 포함된 서명 및 공증된 .pkg 설치 프로그램으로 제공됩니다.",
    requirements: {
      title: "시스템 요구 사항",
      items: [
        "macOS 13 (Ventura) 이상 (v0.42.2부터; macOS 12에서는 v0.42.1을 계속 사용하세요)",
        "Apple Silicon (M1/M2/M3/M4) 또는 Intel (x86_64) 프로세서",
        "최소 4GB RAM (8GB 권장)",
        "200MB 사용 가능한 디스크 공간",
        "관리자 권한 (가상 오디오 드라이버 설치에 필요)",
        "AI 번역 서비스를 위한 인터넷 연결",
        "마이크와 스피커/헤드폰"
      ]
    },
    steps: [
      {
        title: "단계 1: Sokuji 다운로드",
        content: `<a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">공식 GitHub 릴리스 페이지</a>를 방문하여 macOS용 Sokuji 최신 버전을 다운로드하세요.`,
        subsections: [
          {
            title: "PKG 설치 프로그램 다운로드",
            content: `<ul>
              <li><strong>Sokuji-x.x.x-arm64.pkg</strong> - Apple Silicon Mac (M1/M2/M3/M4) 용</li>
              <li><strong>Sokuji-x.x.x-x64.pkg</strong> - Intel Mac 용</li>
              <li>각 설치 프로그램에는 Sokuji 앱과 가상 오디오 드라이버가 포함되어 있습니다</li>
              <li>파일 크기는 약 150-200MB입니다</li>
            </ul>
            <p><strong>팁:</strong> 어떤 버전을 다운로드해야 할지 모르시겠나요? Apple 메뉴() → "이 Mac에 관하여"를 클릭하여 칩 유형을 확인하세요. "Apple M1/M2/M3/M4"가 표시되면 arm64 버전을, "Intel"이 표시되면 x64 버전을 선택하세요.</p>`
          }
        ],
        info: "버전 0.46.5부터 PKG 설치 프로그램은 서명 및 공증되어 있어 macOS에서 추가 단계 없이 열 수 있습니다."
      },
      {
        title: "단계 2: PKG 설치",
        content: `Sokuji와 가상 오디오 드라이버를 설치합니다.`,
        subsections: [
          {
            title: "설치 과정",
            content: `<ol>
              <li>다운로드 폴더에서 다운로드한 <code>.pkg</code> 파일을 찾습니다 (예: <code>Sokuji-x.x.x-arm64.pkg</code> 또는 <code>Sokuji-x.x.x-x64.pkg</code>)</li>
              <li>PKG 파일을 더블 클릭하여 설치를 시작합니다</li>
              <li>macOS 설치 프로그램이 열립니다 - "계속"을 클릭하여 진행합니다</li>
              <li>설치 세부 정보를 검토하고 "설치"를 클릭합니다</li>
              <li>메시지가 표시되면 관리자 비밀번호를 입력합니다 (드라이버 설치에 필요)</li>
              <li>설치가 완료될 때까지 기다립니다 (보통 1-2분)</li>
              <li>설치가 완료되면 "닫기"를 클릭합니다</li>
            </ol>`
          },
          {
            title: "설치되는 항목",
            content: `<ul>
              <li><strong>Sokuji.app</strong> - /Applications에 설치됩니다</li>
              <li><strong>SokujiVirtualAudio.driver</strong> - /Library/Audio/Plug-Ins/HAL에 설치되는 가상 오디오 드라이버</li>
              <li>설치 프로그램이 자동으로 CoreAudio를 재시작하여 드라이버를 로드합니다</li>
            </ul>`
          }
        ],
        info: "설치 프로그램이 시스템 수준 오디오 드라이버를 설치해야 하므로 관리자 비밀번호가 필요합니다. 가상 오디오 드라이버는 고급 오디오 처리 기능을 활성화합니다."
      },
      {
        title: "단계 3: 첫 실행",
        content: `응용 프로그램 폴더에서 Sokuji를 엽니다. 보안 우회 절차는 필요하지 않습니다.`
      },
      {
        title: "단계 4: Sokuji 구성",
        content: `이제 Sokuji가 실행되고 있으므로 첫 사용을 위해 구성해 보겠습니다:`,
        subsections: [
          {
            title: "초기 설정",
            content: `<ol>
              <li>macOS가 메시지를 표시할 때 마이크 권한을 부여합니다</li>
              <li>선호하는 인터페이스 언어를 선택합니다</li>
              <li>AI 공급자(OpenAI, Google Gemini 등)를 선택합니다</li>
              <li>선택한 공급자의 API 키를 입력합니다</li>
              <li>번역을 위한 소스 언어와 대상 언어를 선택합니다</li>
              <li>마이크를 선택하고, <strong>음성</strong> 블록에서 <strong>기본 재생 장치</strong>를 스피커나 헤드폰으로 선택합니다. Sokuji 자체의 가상 오디오 장치는 여기에 표시되지 않습니다. <strong>상대방에게 들리는 번역</strong>이 번역을 그 장치, 즉 회의 앱에서 선택하는 가상 마이크로 보냅니다</li>
              <li>마이크와 스피커 설정을 테스트합니다</li>
            </ol>`
          }
        ],
        success: "축하합니다! 이제 Sokuji를 사용할 준비가 되었습니다. 가상 오디오 드라이버가 설치되었으며 오디오 장치 목록에서 사용할 수 있습니다. 마이크 버튼을 클릭하여 실시간 번역을 시작하세요."
      }
    ],
    troubleshooting: {
      title: "문제 해결",
      issues: [
        {
          title: "마이크가 작동하지 않음",
          content: `Sokuji가 마이크에 액세스할 수 없는 경우:
          <ol>
            <li>시스템 환경설정 → 보안 및 개인 정보 보호 → 개인 정보 보호를 엽니다</li>
            <li>왼쪽 사이드바에서 마이크를 선택합니다</li>
            <li>목록에서 Sokuji가 체크되어 있는지 확인합니다</li>
            <li>목록에 없으면 Sokuji를 다시 시작하고 메시지가 표시되면 권한을 부여합니다</li>
          </ol>`
        },
        {
          title: "오디오 출력 없음",
          content: `번역된 오디오가 들리지 않는 경우:
          <ol>
            <li>시스템 환경설정 → 사운드 → 출력을 엽니다</li>
            <li>선호하는 출력 장치를 선택합니다</li>
            <li>출력 볼륨이 음소거되지 않았는지 확인합니다</li>
            <li>Sokuji의 <strong>음성</strong> 블록에서 듣고 싶은 행의 장치를 확인합니다: 내 번역은 <strong>나도 듣기</strong>(기본값은 꺼짐), 상대방의 번역은 <strong>내가 듣는 번역</strong>, 그 밖의 소리는 <strong>기본 재생 장치</strong></li>
          </ol>`
        },
        {
          title: "Intel Mac에서 앱 충돌 또는 성능 문제",
          content: `Mac에 맞는 올바른 설치 프로그램을 다운로드했는지 확인하세요:
          <ul>
            <li><strong>Apple Silicon</strong> (M1/M2/M3/M4): <code>Sokuji-x.x.x-arm64.pkg</code> 사용</li>
            <li><strong>Intel</strong>: <code>Sokuji-x.x.x-x64.pkg</code> 사용</li>
            <li>칩 유형 확인: Apple 메뉴() → "이 Mac에 관하여"</li>
            <li>잘못된 설치 프로그램을 사용한 경우 <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">릴리스 페이지</a>에서 올바른 버전을 다운로드하세요</li>
            <li>올바른 설치 프로그램으로도 충돌이 계속되면 콘솔.app에서 충돌 로그를 확인하고 GitHub에 보고하세요</li>
          </ul>`
        },
        {
          title: "가상 오디오 드라이버가 표시되지 않음",
          content: `오디오 장치에 "SokujiVirtualAudio"가 표시되지 않는 경우:
          <ol>
            <li>먼저 Sokuji가 복구하도록 하세요: 드라이버는 설치되어 있지만 macOS가 불러오지 않았을 때 Sokuji가 시작 시 이를 알리고 <strong>복구</strong>를 제안합니다. 관리자 비밀번호를 입력하면 Sokuji가 Core Audio를 재시작하여 드라이버를 불러오며, Sokuji를 다시 시작할 필요는 없습니다</li>
            <li>드라이버가 설치되었는지 확인합니다: <code>ls /Library/Audio/Plug-Ins/HAL/</code></li>
            <li>존재하지 않으면 Sokuji를 다시 설치해야 할 수 있습니다</li>
            <li>존재하는데 복구로도 해결되지 않으면 CoreAudio를 재시작해 보세요: <code>sudo killall coreaudiod</code>, 또는 Mac을 다시 시작하세요</li>
            <li>시스템 설정 → 사운드에서 장치가 나타나는지 확인합니다</li>
          </ol>`
        },
        {
          title: "설치에 관리자 비밀번호가 필요함",
          content: `이것은 정상이고 필요합니다:
          <ul>
            <li>PKG 설치 프로그램은 시스템 수준 오디오 드라이버를 설치해야 합니다</li>
            <li>시스템 오디오 드라이버를 설치하려면 관리자 권한이 필요합니다</li>
            <li>메시지가 표시되면 비밀번호를 입력하세요 - 이것은 안전하고 예상되는 동작입니다</li>
            <li>관리자 액세스 권한이 없으면 시스템 관리자에게 문의하세요</li>
          </ul>`
        }
      ]
    },
  },
  es: {
    pageTitle: "Guia de instalacion en macOS",
    langLabel: "Idioma:",
    backLink: "← Volver a la documentacion",
    tocTitle: "Tabla de contenidos",
    mobileJumpTo: "Ir a seccion...",
    overview: "Esta guia le ayudara a instalar Sokuji en sistemas macOS. Sokuji esta disponible como instalador .pkg firmado y notarizado que incluye un controlador de audio virtual para capacidades mejoradas de procesamiento de audio.",
    requirements: {
      title: "Requisitos del sistema",
      items: [
        "macOS 13 (Ventura) o posterior (desde v0.42.2; en macOS 12, siga usando v0.42.1)",
        "Procesador Apple Silicon (M1/M2/M3/M4) o Intel (x86_64)",
        "4GB de RAM minimo (8GB recomendado)",
        "200MB de espacio en disco disponible",
        "Privilegios de administrador (necesarios para la instalacion del controlador de audio virtual)",
        "Conexion a internet para servicios de traduccion con IA",
        "Microfono y altavoces/auriculares"
      ]
    },
    steps: [
      {
        title: "Paso 1: Descargar Sokuji",
        content: `Visite la <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">pagina oficial de lanzamientos en GitHub</a> para descargar la ultima version de Sokuji para macOS.`,
        subsections: [
          {
            title: "Descargar el instalador PKG",
            content: `<ul>
              <li><strong>Sokuji-x.x.x-arm64.pkg</strong> - Para Mac con Apple Silicon (M1/M2/M3/M4)</li>
              <li><strong>Sokuji-x.x.x-x64.pkg</strong> - Para Mac con Intel</li>
              <li>Cada instalador incluye la aplicacion Sokuji y el controlador de audio virtual</li>
              <li>El tamano del archivo es de aproximadamente 150-200MB</li>
            </ul>
            <p><strong>Consejo:</strong> ¿No esta seguro de cual descargar? Haga clic en el menu Apple () → "Acerca de este Mac" para verificar el tipo de chip. Si ve "Apple M1/M2/M3/M4", elija la version arm64. Si ve "Intel", elija la version x64.</p>`
          }
        ],
        info: "Desde la versión 0.46.5 el instalador PKG está firmado y notarizado, así que macOS lo abre sin pasos adicionales."
      },
      {
        title: "Paso 2: Instalar PKG",
        content: `Instale Sokuji y su controlador de audio virtual.`,
        subsections: [
          {
            title: "Proceso de instalacion",
            content: `<ol>
              <li>Localice el archivo <code>.pkg</code> descargado en su carpeta de Descargas (por ejemplo, <code>Sokuji-x.x.x-arm64.pkg</code> o <code>Sokuji-x.x.x-x64.pkg</code>)</li>
              <li>Haga doble clic en el archivo PKG para iniciar la instalacion</li>
              <li>El instalador de macOS se abrira - haga clic en "Continuar" para proceder</li>
              <li>Revise los detalles de la instalacion y haga clic en "Instalar"</li>
              <li>Ingrese su contrasena de administrador cuando se le solicite (necesaria para la instalacion del controlador)</li>
              <li>Espere a que se complete la instalacion (normalmente 1-2 minutos)</li>
              <li>Haga clic en "Cerrar" cuando la instalacion haya finalizado</li>
            </ol>`
          },
          {
            title: "Que se instala",
            content: `<ul>
              <li><strong>Sokuji.app</strong> - Instalado en /Applications</li>
              <li><strong>SokujiVirtualAudio.driver</strong> - Controlador de audio virtual instalado en /Library/Audio/Plug-Ins/HAL</li>
              <li>El instalador reiniciara automaticamente CoreAudio para cargar el controlador</li>
            </ul>`
          }
        ],
        info: "Se requiere la contrasena de administrador porque el instalador necesita instalar un controlador de audio a nivel del sistema. El controlador de audio virtual habilita funciones avanzadas de procesamiento de audio."
      },
      {
        title: "Paso 3: Primer lanzamiento",
        content: `Abra Sokuji desde la carpeta Aplicaciones. No hace falta ningún paso de seguridad adicional.`
      },
      {
        title: "Paso 4: Configurar Sokuji",
        content: `Ahora que Sokuji esta ejecutandose, configuremoslo para el primer uso:`,
        subsections: [
          {
            title: "Configuracion inicial",
            content: `<ol>
              <li>Otorgue permisos de microfono cuando macOS se lo solicite</li>
              <li>Seleccione su idioma de interfaz preferido</li>
              <li>Elija su proveedor de IA (OpenAI, Google Gemini, etc.)</li>
              <li>Ingrese su clave API para el proveedor seleccionado</li>
              <li>Seleccione los idiomas de origen y destino para la traduccion</li>
              <li>Seleccione su micrófono y, en el bloque <strong>Voz</strong>, elija sus altavoces o auriculares como <strong>Dispositivo de reproducción predeterminado</strong>. Sokuji no muestra ahí su propio dispositivo de audio virtual: <strong>Traducción que oye la otra parte</strong> envía su traducción a él, como el micrófono virtual que elige en su aplicación de reuniones</li>
              <li>Pruebe la configuracion de su microfono y altavoces</li>
            </ol>`
          }
        ],
        success: "¡Felicidades! Sokuji esta listo para usar. El controlador de audio virtual ha sido instalado y esta disponible en su lista de dispositivos de audio. Haga clic en el boton del microfono para iniciar la traduccion en tiempo real."
      }
    ],
    troubleshooting: {
      title: "Solucion de problemas",
      issues: [
        {
          title: "El microfono no funciona",
          content: `Si Sokuji no puede acceder a su microfono:
          <ol>
            <li>Abra Preferencias del Sistema → Seguridad y Privacidad → Privacidad</li>
            <li>Seleccione Microfono en la barra lateral izquierda</li>
            <li>Asegurese de que Sokuji este marcado en la lista</li>
            <li>Si no esta en la lista, reinicie Sokuji y otorgue permiso cuando se le solicite</li>
          </ol>`
        },
        {
          title: "Sin salida de audio",
          content: `Si no puede escuchar el audio traducido:
          <ol>
            <li>Abra Preferencias del Sistema → Sonido → Salida</li>
            <li>Seleccione su dispositivo de salida preferido</li>
            <li>Verifique que el volumen de salida no este silenciado</li>
            <li>En el bloque <strong>Voz</strong> de Sokuji, revise el dispositivo de la fila que espera oír: <strong>Yo también la oigo</strong> para su propia traducción (desactivada por defecto), <strong>Traducción que yo oigo</strong> para la de la otra parte y <strong>Dispositivo de reproducción predeterminado</strong> para todo lo demás</li>
          </ol>`
        },
        {
          title: "La aplicacion se bloquea en Mac Intel o problemas de rendimiento",
          content: `Asegurese de haber descargado el instalador correcto para su Mac:
          <ul>
            <li><strong>Apple Silicon</strong> (M1/M2/M3/M4): Use <code>Sokuji-x.x.x-arm64.pkg</code></li>
            <li><strong>Intel</strong>: Use <code>Sokuji-x.x.x-x64.pkg</code></li>
            <li>Verifique su tipo de chip: menu Apple () → "Acerca de este Mac"</li>
            <li>Si uso el instalador incorrecto, descargue la version correcta de la <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">pagina de lanzamientos</a></li>
            <li>Si los bloqueos persisten con el instalador correcto, revise Console.app para los registros de bloqueo y reportelos en GitHub</li>
          </ul>`
        },
        {
          title: "El controlador de audio virtual no aparece",
          content: `Si no ve "SokujiVirtualAudio" en sus dispositivos de audio:
          <ol>
            <li>Deje primero que Sokuji lo repare: cuando el controlador esta instalado pero macOS no lo ha cargado, Sokuji lo indica al iniciar y ofrece <strong>Reparar</strong>. Ingrese su contrasena de administrador y Sokuji reiniciara Core Audio para cargar el controlador, sin necesidad de reiniciar Sokuji</li>
            <li>Verifique si el controlador esta instalado: <code>ls /Library/Audio/Plug-Ins/HAL/</code></li>
            <li>Si no esta presente, es posible que necesite reinstalar Sokuji</li>
            <li>Si esta presente y Reparar no ayudo, intente reiniciar CoreAudio: <code>sudo killall coreaudiod</code>, o reinicie el Mac</li>
            <li>Revise Ajustes del Sistema → Sonido para ver si el dispositivo aparece</li>
          </ol>`
        },
        {
          title: "La instalacion requiere contrasena de administrador",
          content: `Esto es normal y necesario:
          <ul>
            <li>El instalador PKG necesita instalar un controlador de audio a nivel del sistema</li>
            <li>Los controladores de audio del sistema requieren privilegios de administrador para instalarse</li>
            <li>Ingrese su contrasena cuando se le solicite - esto es seguro y esperado</li>
            <li>Si no tiene acceso de administrador, contacte a su administrador del sistema</li>
          </ul>`
        }
      ]
    },
  },
  fr: {
    pageTitle: "Guide d'installation macOS",
    langLabel: "Langue :",
    backLink: "← Retour a la documentation",
    tocTitle: "Table des matieres",
    mobileJumpTo: "Aller a la section...",
    overview: "Ce guide vous accompagnera dans l'installation de Sokuji sur les systemes macOS. Sokuji est disponible sous forme d'installateur .pkg signé et notarié qui inclut un pilote audio virtuel pour des capacites de traitement audio ameliorees.",
    requirements: {
      title: "Configuration requise",
      items: [
        "macOS 13 (Ventura) ou ultérieur (depuis v0.42.2 ; sous macOS 12, continuez d'utiliser v0.42.1)",
        "Processeur Apple Silicon (M1/M2/M3/M4) ou Intel (x86_64)",
        "4 Go de RAM minimum (8 Go recommandes)",
        "200 Mo d'espace disque disponible",
        "Privileges d'administrateur (requis pour l'installation du pilote audio virtuel)",
        "Connexion Internet pour les services de traduction IA",
        "Microphone et haut-parleurs/ecouteurs"
      ]
    },
    steps: [
      {
        title: "Etape 1 : Telecharger Sokuji",
        content: `Visitez la <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">page officielle des versions sur GitHub</a> pour telecharger la derniere version de Sokuji pour macOS.`,
        subsections: [
          {
            title: "Telecharger l'installateur PKG",
            content: `<ul>
              <li><strong>Sokuji-x.x.x-arm64.pkg</strong> - Pour les Mac Apple Silicon (M1/M2/M3/M4)</li>
              <li><strong>Sokuji-x.x.x-x64.pkg</strong> - Pour les Mac Intel</li>
              <li>Chaque installateur comprend l'application Sokuji et le pilote audio virtuel</li>
              <li>La taille du fichier est d'environ 150-200 Mo</li>
            </ul>
            <p><strong>Conseil :</strong> Vous ne savez pas lequel telecharger ? Cliquez sur le menu Apple () → "A propos de ce Mac" pour verifier votre type de puce. Si vous voyez "Apple M1/M2/M3/M4", choisissez la version arm64. Si vous voyez "Intel", choisissez la version x64.</p>`
          }
        ],
        info: "Depuis la version 0.46.5, l'installateur PKG est signé et notarié : macOS l'ouvre sans étape supplémentaire."
      },
      {
        title: "Etape 2 : Installer le PKG",
        content: `Installez Sokuji et son pilote audio virtuel.`,
        subsections: [
          {
            title: "Processus d'installation",
            content: `<ol>
              <li>Localisez le fichier <code>.pkg</code> telecharge dans votre dossier Telechargements (par ex., <code>Sokuji-x.x.x-arm64.pkg</code> ou <code>Sokuji-x.x.x-x64.pkg</code>)</li>
              <li>Double-cliquez sur le fichier PKG pour demarrer l'installation</li>
              <li>L'installateur macOS s'ouvrira - cliquez sur "Continuer" pour poursuivre</li>
              <li>Examinez les details de l'installation et cliquez sur "Installer"</li>
              <li>Saisissez votre mot de passe administrateur lorsque demande (requis pour l'installation du pilote)</li>
              <li>Attendez que l'installation se termine (generalement 1-2 minutes)</li>
              <li>Cliquez sur "Fermer" lorsque l'installation est terminee</li>
            </ol>`
          },
          {
            title: "Ce qui est installe",
            content: `<ul>
              <li><strong>Sokuji.app</strong> - Installe dans /Applications</li>
              <li><strong>SokujiVirtualAudio.driver</strong> - Pilote audio virtuel installe dans /Library/Audio/Plug-Ins/HAL</li>
              <li>L'installateur redemarrera automatiquement CoreAudio pour charger le pilote</li>
            </ul>`
          }
        ],
        info: "Le mot de passe administrateur est requis car l'installateur doit installer un pilote audio au niveau du systeme. Le pilote audio virtuel active des fonctionnalites avancees de traitement audio."
      },
      {
        title: "Etape 3 : Premier lancement",
        content: `Ouvrez Sokuji depuis le dossier Applications. Aucune étape de sécurité supplémentaire n'est nécessaire.`
      },
      {
        title: "Etape 4 : Configurer Sokuji",
        content: `Maintenant que Sokuji est en cours d'execution, configurons-le pour la premiere utilisation :`,
        subsections: [
          {
            title: "Configuration initiale",
            content: `<ol>
              <li>Accordez les autorisations du microphone lorsque macOS vous le demande</li>
              <li>Selectionnez votre langue d'interface preferee</li>
              <li>Choisissez votre fournisseur d'IA (OpenAI, Google Gemini, etc.)</li>
              <li>Saisissez votre cle API pour le fournisseur selectionne</li>
              <li>Selectionnez les langues source et cible pour la traduction</li>
              <li>Sélectionnez votre microphone et, dans le bloc <strong>Voix</strong>, choisissez vos haut-parleurs ou votre casque comme <strong>Appareil de lecture par défaut</strong>. Sokuji n'y affiche pas son propre périphérique audio virtuel : <strong>Traduction entendue par l'autre</strong> y envoie votre traduction, c'est le micro virtuel que vous choisissez dans votre application de réunion</li>
              <li>Testez votre configuration microphone et haut-parleurs</li>
            </ol>`
          }
        ],
        success: "Felicitations ! Sokuji est maintenant pret a l'emploi. Le pilote audio virtuel a ete installe et est disponible dans votre liste de peripheriques audio. Cliquez sur le bouton du microphone pour demarrer la traduction en temps reel."
      }
    ],
    troubleshooting: {
      title: "Depannage",
      issues: [
        {
          title: "Le microphone ne fonctionne pas",
          content: `Si Sokuji ne peut pas acceder a votre microphone :
          <ol>
            <li>Ouvrez Preferences Systeme → Securite et confidentialite → Confidentialite</li>
            <li>Selectionnez Microphone dans la barre laterale gauche</li>
            <li>Assurez-vous que Sokuji est coche dans la liste</li>
            <li>S'il n'est pas dans la liste, redemarrez Sokuji et accordez l'autorisation lorsque demande</li>
          </ol>`
        },
        {
          title: "Pas de sortie audio",
          content: `Si vous ne pouvez pas entendre l'audio traduit :
          <ol>
            <li>Ouvrez Preferences Systeme → Son → Sortie</li>
            <li>Selectionnez votre peripherique de sortie prefere</li>
            <li>Verifiez que le volume de sortie n'est pas coupe</li>
            <li>Dans le bloc <strong>Voix</strong> de Sokuji, vérifiez l'appareil de la ligne que vous voulez entendre : <strong>Je l'entends aussi</strong> pour votre propre traduction (désactivé par défaut), <strong>Traduction que j'entends</strong> pour celle de l'autre, et <strong>Appareil de lecture par défaut</strong> pour tout le reste</li>
          </ol>`
        },
        {
          title: "L'application plante sur Mac Intel ou problemes de performances",
          content: `Assurez-vous d'avoir telecharge le bon installateur pour votre Mac :
          <ul>
            <li><strong>Apple Silicon</strong> (M1/M2/M3/M4) : Utilisez <code>Sokuji-x.x.x-arm64.pkg</code></li>
            <li><strong>Intel</strong> : Utilisez <code>Sokuji-x.x.x-x64.pkg</code></li>
            <li>Verifiez votre type de puce : menu Apple () → "A propos de ce Mac"</li>
            <li>Si vous avez utilise le mauvais installateur, telechargez la bonne version depuis la <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">page des versions</a></li>
            <li>Si les plantages persistent avec le bon installateur, consultez Console.app pour les journaux de plantage et signalez-les sur GitHub</li>
          </ul>`
        },
        {
          title: "Le pilote audio virtuel n'apparait pas",
          content: `Si vous ne voyez pas "SokujiVirtualAudio" dans vos peripheriques audio :
          <ol>
            <li>Laissez d'abord Sokuji le réparer : quand le pilote est installé mais que macOS ne l'a pas chargé, Sokuji le signale au démarrage et propose <strong>Réparer</strong>. Entrez votre mot de passe administrateur et Sokuji redémarre Core Audio pour charger le pilote, sans avoir à redémarrer Sokuji</li>
            <li>Verifiez si le pilote est installe : <code>ls /Library/Audio/Plug-Ins/HAL/</code></li>
            <li>S'il n'est pas present, vous devrez peut-etre reinstaller Sokuji</li>
            <li>S'il est present et que Réparer n'a pas aidé, essayez de redemarrer CoreAudio : <code>sudo killall coreaudiod</code>, ou redémarrez le Mac</li>
            <li>Verifiez Réglages Système → Son pour voir si le peripherique apparait</li>
          </ol>`
        },
        {
          title: "L'installation necessite un mot de passe administrateur",
          content: `C'est normal et necessaire :
          <ul>
            <li>L'installateur PKG doit installer un pilote audio au niveau du systeme</li>
            <li>Les pilotes audio systeme necessitent des privileges d'administrateur pour l'installation</li>
            <li>Saisissez votre mot de passe lorsque demande - c'est sur et attendu</li>
            <li>Si vous n'avez pas d'acces administrateur, contactez votre administrateur systeme</li>
          </ul>`
        }
      ]
    },
  },
  ar: {
    pageTitle: "دليل تثبيت macOS",
    langLabel: "اللغة:",
    backLink: "← العودة الى التوثيق",
    tocTitle: "جدول المحتويات",
    mobileJumpTo: "الانتقال الى قسم...",
    overview: "سيرشدك هذا الدليل خلال تثبيت Sokuji على انظمة macOS. يتوفر Sokuji كمثبت .pkg موقّع وموثّق من Apple يتضمن برنامج تشغيل صوت افتراضي لقدرات معالجة صوت محسنة.",
    requirements: {
      title: "متطلبات النظام",
      items: [
        "⁦macOS 13 (Ventura)⁩ أو أحدث (منذ ⁦v0.42.2⁩؛ على macOS 12 واصل استخدام ⁦v0.42.1⁩)",
        "معالج Apple Silicon (M1/M2/M3/M4) او Intel (x86_64)",
        "4 جيجابايت رام كحد ادنى (8 جيجابايت موصى بها)",
        "200 ميجابايت مساحة قرص متاحة",
        "صلاحيات المسؤول (مطلوبة لتثبيت برنامج تشغيل الصوت الافتراضي)",
        "اتصال بالانترنت لخدمات الترجمة بالذكاء الاصطناعي",
        "ميكروفون ومكبرات صوت/سماعات"
      ]
    },
    steps: [
      {
        title: "الخطوة 1: تحميل Sokuji",
        content: `قم بزيارة <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">صفحة الاصدارات الرسمية على GitHub</a> لتحميل احدث اصدار من Sokuji لنظام macOS.`,
        subsections: [
          {
            title: "تحميل مثبت PKG",
            content: `<ul>
              <li><strong>Sokuji-x.x.x-arm64.pkg</strong> - لاجهزة Mac بمعالج Apple Silicon (M1/M2/M3/M4)</li>
              <li><strong>Sokuji-x.x.x-x64.pkg</strong> - لاجهزة Mac بمعالج Intel</li>
              <li>يتضمن كل مثبت تطبيق Sokuji وبرنامج تشغيل الصوت الافتراضي</li>
              <li>حجم الملف حوالي 150-200 ميجابايت</li>
            </ul>
            <p><strong>نصيحة:</strong> غير متاكد من الاصدار المناسب؟ انقر على قائمة Apple () ← "حول هذا الـ Mac" للتحقق من نوع المعالج. اذا رايت "Apple M1/M2/M3/M4"، اختر اصدار arm64. اذا رايت "Intel"، اختر اصدار x64.</p>`
          }
        ],
        info: "منذ الإصدار 0.46.5 أصبح مثبت PKG موقّعا وموثّقا، لذا يفتحه macOS دون خطوات إضافية."
      },
      {
        title: "الخطوة 2: تثبيت PKG",
        content: `ثبّت Sokuji وبرنامج تشغيل الصوت الافتراضي.`,
        subsections: [
          {
            title: "عملية التثبيت",
            content: `<ol>
              <li>حدد موقع ملف <code>.pkg</code> المحمل في مجلد التنزيلات (مثل <code>Sokuji-x.x.x-arm64.pkg</code> او <code>Sokuji-x.x.x-x64.pkg</code>)</li>
              <li>انقر نقرا مزدوجا على ملف PKG لبدء التثبيت</li>
              <li>سيفتح مثبت macOS - انقر على "متابعة" للمتابعة</li>
              <li>راجع تفاصيل التثبيت وانقر على "تثبيت"</li>
              <li>ادخل كلمة مرور المسؤول عند المطالبة (مطلوبة لتثبيت برنامج التشغيل)</li>
              <li>انتظر حتى يكتمل التثبيت (عادة 1-2 دقيقة)</li>
              <li>انقر على "اغلاق" عند انتهاء التثبيت</li>
            </ol>`
          },
          {
            title: "ما يتم تثبيته",
            content: `<ul>
              <li><strong>Sokuji.app</strong> - يتم تثبيته في /Applications</li>
              <li><strong>SokujiVirtualAudio.driver</strong> - برنامج تشغيل الصوت الافتراضي يتم تثبيته في /Library/Audio/Plug-Ins/HAL</li>
              <li>سيقوم المثبت تلقائيا باعادة تشغيل CoreAudio لتحميل برنامج التشغيل</li>
            </ul>`
          }
        ],
        info: "كلمة مرور المسؤول مطلوبة لان المثبت يحتاج الى تثبيت برنامج تشغيل صوت على مستوى النظام. يمكّن برنامج تشغيل الصوت الافتراضي ميزات معالجة الصوت المتقدمة."
      },
      {
        title: "الخطوة 3: التشغيل الاول",
        content: `افتح Sokuji من مجلد التطبيقات. لا حاجة إلى أي خطوات أمان إضافية.`
      },
      {
        title: "الخطوة 4: تكوين Sokuji",
        content: `الآن بعد تشغيل Sokuji، لنقم بتكوينه للاستخدام الاول:`,
        subsections: [
          {
            title: "الاعداد الاولي",
            content: `<ol>
              <li>امنح اذونات الميكروفون عندما يطلب macOS ذلك</li>
              <li>حدد لغة الواجهة المفضلة لديك</li>
              <li>اختر مزود الذكاء الاصطناعي (OpenAI، Google Gemini، الخ.)</li>
              <li>ادخل مفتاح API للمزود المحدد</li>
              <li>حدد لغات المصدر والهدف للترجمة</li>
              <li>اختر الميكروفون، وفي قسم <strong>الكلام</strong> اختر مكبرات الصوت أو السماعات كـ <strong>جهاز التشغيل الافتراضي</strong>. لا يعرض Sokuji هناك جهاز الصوت الافتراضي الخاص به: <strong>الترجمة التي يسمعها الطرف الآخر</strong> ترسل ترجمتك إليه، فهو الميكروفون الافتراضي الذي تختاره في تطبيق الاجتماعات</li>
              <li>اختبر اعداد الميكروفون ومكبرات الصوت</li>
            </ol>`
          }
        ],
        success: "تهانينا! Sokuji جاهز الآن للاستخدام. تم تثبيت برنامج تشغيل الصوت الافتراضي وهو متاح في قائمة اجهزة الصوت. انقر على زر الميكروفون لبدء الترجمة في الوقت الفعلي."
      }
    ],
    troubleshooting: {
      title: "استكشاف الاخطاء واصلاحها",
      issues: [
        {
          title: "الميكروفون لا يعمل",
          content: `اذا لم يتمكن Sokuji من الوصول الى الميكروفون:
          <ol>
            <li>افتح تفضيلات النظام ← الامان والخصوصية ← الخصوصية</li>
            <li>حدد الميكروفون من الشريط الجانبي الايسر</li>
            <li>تاكد من ان Sokuji محدد في القائمة</li>
            <li>اذا لم يكن مدرجا، اعد تشغيل Sokuji وامنح الاذن عند المطالبة</li>
          </ol>`
        },
        {
          title: "لا يوجد اخراج صوتي",
          content: `اذا لم تتمكن من سماع الصوت المترجم:
          <ol>
            <li>افتح تفضيلات النظام ← الصوت ← الاخراج</li>
            <li>حدد جهاز الاخراج المفضل لديك</li>
            <li>تحقق من ان حجم الاخراج غير مكتوم</li>
            <li>في قسم <strong>الكلام</strong> في Sokuji، تحقق من جهاز الصف الذي تتوقع سماعه: <strong>أسمعها أنا أيضًا</strong> لترجمتك أنت (متوقف افتراضيًا)، و<strong>الترجمة التي أسمعها</strong> لترجمة الطرف الآخر، و<strong>جهاز التشغيل الافتراضي</strong> لكل ما عدا ذلك</li>
          </ol>`
        },
        {
          title: "التطبيق يتعطل على اجهزة Mac بمعالج Intel او مشاكل في الاداء",
          content: `تاكد من تحميل المثبت الصحيح لجهاز Mac الخاص بك:
          <ul>
            <li><strong>Apple Silicon</strong> (M1/M2/M3/M4): استخدم <code>Sokuji-x.x.x-arm64.pkg</code></li>
            <li><strong>Intel</strong>: استخدم <code>Sokuji-x.x.x-x64.pkg</code></li>
            <li>تحقق من نوع المعالج: قائمة Apple () ← "حول هذا الـ Mac"</li>
            <li>اذا استخدمت المثبت الخاطئ، حمل الاصدار الصحيح من <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">صفحة الاصدارات</a></li>
            <li>اذا استمرت الاعطال مع المثبت الصحيح، تحقق من Console.app لسجلات الاعطال وابلغ عنها على GitHub</li>
          </ul>`
        },
        {
          title: "برنامج تشغيل الصوت الافتراضي لا يظهر",
          content: `اذا لم ترَ "SokujiVirtualAudio" في اجهزة الصوت:
          <ol>
            <li>دع Sokuji يصلحه أولًا: عندما يكون برنامج التشغيل مثبتًا لكن macOS لم يحمّله، يخبرك Sokuji بذلك عند بدء التشغيل ويعرض عليك <strong>إصلاح</strong>. أدخل كلمة مرور المسؤول، فيعيد Sokuji تشغيل Core Audio لتحميل برنامج التشغيل، دون الحاجة إلى إعادة تشغيل Sokuji</li>
            <li>تحقق مما اذا كان برنامج التشغيل مثبتا: <code>ls /Library/Audio/Plug-Ins/HAL/</code></li>
            <li>اذا لم يكن موجودا، قد تحتاج الى اعادة تثبيت Sokuji</li>
            <li>اذا كان موجودا ولم يُجدِ الإصلاح، حاول اعادة تشغيل CoreAudio: <code>sudo killall coreaudiod</code>، أو أعد تشغيل جهاز Mac</li>
            <li>تحقق من إعدادات النظام ← الصوت لمعرفة ما اذا كان الجهاز يظهر</li>
          </ol>`
        },
        {
          title: "التثبيت يتطلب كلمة مرور المسؤول",
          content: `هذا طبيعي ومطلوب:
          <ul>
            <li>يحتاج مثبت PKG الى تثبيت برنامج تشغيل صوت على مستوى النظام</li>
            <li>تتطلب برامج تشغيل الصوت على مستوى النظام صلاحيات المسؤول للتثبيت</li>
            <li>ادخل كلمة المرور عند المطالبة - هذا آمن ومتوقع</li>
            <li>اذا لم يكن لديك صلاحيات المسؤول، اتصل بمسؤول النظام</li>
          </ul>`
        }
      ]
    },
  },
  pt: {
    pageTitle: "Guia de Instalacao no macOS",
    langLabel: "Idioma:",
    backLink: "← Voltar a documentacao",
    tocTitle: "Indice",
    mobileJumpTo: "Ir para secao...",
    overview: "Este guia orientara voce na instalacao do Sokuji em sistemas macOS. O Sokuji esta disponivel como instalador .pkg assinado e notarizado que inclui um driver de audio virtual para recursos aprimorados de processamento de audio.",
    requirements: {
      title: "Requisitos do Sistema",
      items: [
        "macOS 13 (Ventura) ou posterior (desde a v0.42.2; no macOS 12, continue usando a v0.42.1)",
        "Processador Apple Silicon (M1/M2/M3/M4) ou Intel (x86_64)",
        "4GB de RAM minimo (8GB recomendado)",
        "200MB de espaco em disco disponivel",
        "Privilegios de administrador (necessarios para instalacao do driver de audio virtual)",
        "Conexao com a internet para servicos de traducao com IA",
        "Microfone e alto-falantes/fones de ouvido"
      ]
    },
    steps: [
      {
        title: "Passo 1: Baixar o Sokuji",
        content: `Visite a <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">pagina oficial de lancamentos no GitHub</a> para baixar a versao mais recente do Sokuji para macOS.`,
        subsections: [
          {
            title: "Baixar o instalador PKG",
            content: `<ul>
              <li><strong>Sokuji-x.x.x-arm64.pkg</strong> - Para Macs com Apple Silicon (M1/M2/M3/M4)</li>
              <li><strong>Sokuji-x.x.x-x64.pkg</strong> - Para Macs com Intel</li>
              <li>Cada instalador inclui o aplicativo Sokuji e o driver de audio virtual</li>
              <li>O tamanho do arquivo e de aproximadamente 150-200MB</li>
            </ul>
            <p><strong>Dica:</strong> Nao sabe qual baixar? Clique no menu Apple () → "Sobre Este Mac" para verificar o tipo do chip. Se voce vir "Apple M1/M2/M3/M4", escolha a versao arm64. Se vir "Intel", escolha a versao x64.</p>`
          }
        ],
        info: "Desde a versão 0.46.5 o instalador PKG é assinado e notarizado, então o macOS o abre sem etapas adicionais."
      },
      {
        title: "Passo 2: Instalar o PKG",
        content: `Instale o Sokuji e seu driver de áudio virtual.`,
        subsections: [
          {
            title: "Processo de instalacao",
            content: `<ol>
              <li>Localize o arquivo <code>.pkg</code> baixado na pasta Downloads (ex.: <code>Sokuji-x.x.x-arm64.pkg</code> ou <code>Sokuji-x.x.x-x64.pkg</code>)</li>
              <li>Clique duas vezes no arquivo PKG para iniciar a instalacao</li>
              <li>O instalador do macOS sera aberto - clique em "Continuar" para prosseguir</li>
              <li>Revise os detalhes da instalacao e clique em "Instalar"</li>
              <li>Digite sua senha de administrador quando solicitado (necessaria para a instalacao do driver)</li>
              <li>Aguarde a conclusao da instalacao (geralmente 1-2 minutos)</li>
              <li>Clique em "Fechar" quando a instalacao estiver concluida</li>
            </ol>`
          },
          {
            title: "O que e instalado",
            content: `<ul>
              <li><strong>Sokuji.app</strong> - Instalado em /Applications</li>
              <li><strong>SokujiVirtualAudio.driver</strong> - Driver de audio virtual instalado em /Library/Audio/Plug-Ins/HAL</li>
              <li>O instalador reiniciara automaticamente o CoreAudio para carregar o driver</li>
            </ul>`
          }
        ],
        info: "A senha de administrador e necessaria porque o instalador precisa instalar um driver de audio no nivel do sistema. O driver de audio virtual habilita recursos avancados de processamento de audio."
      },
      {
        title: "Passo 3: Primeiro lançamento",
        content: `Abra o Sokuji pela pasta Aplicativos. Nenhuma etapa de segurança adicional é necessária.`
      },
      {
        title: "Passo 4: Configurar o Sokuji",
        content: `Agora que o Sokuji esta em execucao, vamos configura-lo para o primeiro uso:`,
        subsections: [
          {
            title: "Configuracao inicial",
            content: `<ol>
              <li>Conceda permissoes de microfone quando o macOS solicitar</li>
              <li>Selecione seu idioma de interface preferido</li>
              <li>Escolha seu provedor de IA (OpenAI, Google Gemini, etc.)</li>
              <li>Insira sua chave API para o provedor selecionado</li>
              <li>Selecione os idiomas de origem e destino para traducao</li>
              <li>Selecione seu microfone e, no bloco <strong>Voz</strong>, escolha seus alto-falantes ou fones de ouvido como <strong>Dispositivo de reprodução padrão</strong>. O Sokuji não lista ali o próprio dispositivo de áudio virtual: <strong>Tradução que o outro lado ouve</strong> envia sua tradução para ele, o microfone virtual que você escolhe no aplicativo de reunião</li>
              <li>Teste a configuracao do microfone e alto-falantes</li>
            </ol>`
          }
        ],
        success: "Parabens! O Sokuji esta pronto para uso. O driver de audio virtual foi instalado e esta disponivel na sua lista de dispositivos de audio. Clique no botao do microfone para iniciar a traducao em tempo real."
      }
    ],
    troubleshooting: {
      title: "Solucao de Problemas",
      issues: [
        {
          title: "Microfone nao funciona",
          content: `Se o Sokuji nao consegue acessar seu microfone:
          <ol>
            <li>Abra Preferencias do Sistema → Seguranca e Privacidade → Privacidade</li>
            <li>Selecione Microfone na barra lateral esquerda</li>
            <li>Certifique-se de que o Sokuji esteja marcado na lista</li>
            <li>Se nao estiver listado, reinicie o Sokuji e conceda permissao quando solicitado</li>
          </ol>`
        },
        {
          title: "Sem saida de audio",
          content: `Se voce nao consegue ouvir o audio traduzido:
          <ol>
            <li>Abra Preferencias do Sistema → Som → Saida</li>
            <li>Selecione seu dispositivo de saida preferido</li>
            <li>Verifique se o volume de saida nao esta mudo</li>
            <li>No bloco <strong>Voz</strong> do Sokuji, confira o dispositivo da linha que você espera ouvir: <strong>Eu também ouço</strong> para a sua própria tradução (desativado por padrão), <strong>Tradução que eu ouço</strong> para a do outro lado e <strong>Dispositivo de reprodução padrão</strong> para todo o resto</li>
          </ol>`
        },
        {
          title: "Aplicativo trava em Macs Intel ou problemas de desempenho",
          content: `Certifique-se de ter baixado o instalador correto para seu Mac:
          <ul>
            <li><strong>Apple Silicon</strong> (M1/M2/M3/M4): Use <code>Sokuji-x.x.x-arm64.pkg</code></li>
            <li><strong>Intel</strong>: Use <code>Sokuji-x.x.x-x64.pkg</code></li>
            <li>Verifique o tipo do chip: menu Apple () → "Sobre Este Mac"</li>
            <li>Se usou o instalador errado, baixe a versao correta da <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">pagina de lancamentos</a></li>
            <li>Se os travamentos persistirem com o instalador correto, verifique o Console.app para logs de travamento e reporte-os no GitHub</li>
          </ul>`
        },
        {
          title: "Driver de audio virtual nao aparece",
          content: `Se voce nao ve "SokujiVirtualAudio" nos seus dispositivos de audio:
          <ol>
            <li>Deixe o Sokuji reparar primeiro: quando o driver está instalado mas o macOS não o carregou, o Sokuji avisa ao iniciar e oferece <strong>Reparar</strong>. Digite sua senha de administrador e o Sokuji reinicia o Core Audio para carregar o driver, sem precisar reiniciar o Sokuji</li>
            <li>Verifique se o driver esta instalado: <code>ls /Library/Audio/Plug-Ins/HAL/</code></li>
            <li>Se nao estiver presente, voce pode precisar reinstalar o Sokuji</li>
            <li>Se estiver presente e Reparar não ajudou, tente reiniciar o CoreAudio: <code>sudo killall coreaudiod</code>, ou reinicie o Mac</li>
            <li>Verifique Ajustes do Sistema → Som para ver se o dispositivo aparece</li>
          </ol>`
        },
        {
          title: "Instalacao requer senha de administrador",
          content: `Isso e normal e necessario:
          <ul>
            <li>O instalador PKG precisa instalar um driver de audio no nivel do sistema</li>
            <li>Drivers de audio do sistema requerem privilegios de administrador para instalacao</li>
            <li>Digite sua senha quando solicitado - isso e seguro e esperado</li>
            <li>Se voce nao tem acesso de administrador, entre em contato com seu administrador do sistema</li>
          </ul>`
        }
      ]
    },
  },
  ru: {
    pageTitle: "Руководство по установке на macOS",
    langLabel: "Язык:",
    backLink: "← Назад к документации",
    tocTitle: "Содержание",
    mobileJumpTo: "Перейти к разделу...",
    overview: "Это руководство поможет вам установить Sokuji на системы macOS. Sokuji доступен как подписанный и нотаризованный установщик .pkg, который включает виртуальный аудиодрайвер для расширенных возможностей обработки звука.",
    requirements: {
      title: "Системные требования",
      items: [
        "macOS 13 (Ventura) или новее (начиная с v0.42.2; на macOS 12 продолжайте пользоваться v0.42.1)",
        "Процессор Apple Silicon (M1/M2/M3/M4) или Intel (x86_64)",
        "Минимум 4 ГБ оперативной памяти (рекомендуется 8 ГБ)",
        "200 МБ свободного дискового пространства",
        "Права администратора (необходимы для установки виртуального аудиодрайвера)",
        "Подключение к интернету для служб перевода с ИИ",
        "Микрофон и динамики/наушники"
      ]
    },
    steps: [
      {
        title: "Шаг 1: Скачать Sokuji",
        content: `Посетите <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">официальную страницу релизов на GitHub</a>, чтобы скачать последнюю версию Sokuji для macOS.`,
        subsections: [
          {
            title: "Скачать установщик PKG",
            content: `<ul>
              <li><strong>Sokuji-x.x.x-arm64.pkg</strong> - Для Mac с Apple Silicon (M1/M2/M3/M4)</li>
              <li><strong>Sokuji-x.x.x-x64.pkg</strong> - Для Mac с Intel</li>
              <li>Каждый установщик включает приложение Sokuji и виртуальный аудиодрайвер</li>
              <li>Размер файла примерно 150-200 МБ</li>
            </ul>
            <p><strong>Совет:</strong> Не уверены, какой скачать? Нажмите меню Apple () → «Об этом Mac», чтобы проверить тип чипа. Если вы видите «Apple M1/M2/M3/M4», выберите версию arm64. Если видите «Intel», выберите версию x64.</p>`
          }
        ],
        info: "Начиная с версии 0.46.5 установщик PKG подписан и нотаризован, поэтому macOS открывает его без дополнительных шагов."
      },
      {
        title: "Шаг 2: Установить PKG",
        content: `Установите Sokuji и его виртуальный аудиодрайвер.`,
        subsections: [
          {
            title: "Процесс установки",
            content: `<ol>
              <li>Найдите загруженный файл <code>.pkg</code> в папке Загрузки (например, <code>Sokuji-x.x.x-arm64.pkg</code> или <code>Sokuji-x.x.x-x64.pkg</code>)</li>
              <li>Дважды щелкните по файлу PKG, чтобы начать установку</li>
              <li>Откроется установщик macOS — нажмите «Продолжить» для продолжения</li>
              <li>Просмотрите детали установки и нажмите «Установить»</li>
              <li>Введите пароль администратора при запросе (необходим для установки драйвера)</li>
              <li>Дождитесь завершения установки (обычно 1-2 минуты)</li>
              <li>Нажмите «Закрыть» после завершения установки</li>
            </ol>`
          },
          {
            title: "Что устанавливается",
            content: `<ul>
              <li><strong>Sokuji.app</strong> - Устанавливается в /Applications</li>
              <li><strong>SokujiVirtualAudio.driver</strong> - Виртуальный аудиодрайвер, устанавливаемый в /Library/Audio/Plug-Ins/HAL</li>
              <li>Установщик автоматически перезапустит CoreAudio для загрузки драйвера</li>
            </ul>`
          }
        ],
        info: "Пароль администратора необходим, потому что установщику нужно установить системный аудиодрайвер. Виртуальный аудиодрайвер обеспечивает расширенные функции обработки звука."
      },
      {
        title: "Шаг 3: Первый запуск",
        content: `Откройте Sokuji из папки «Программы». Дополнительные шаги безопасности не нужны.`
      },
      {
        title: "Шаг 4: Настроить Sokuji",
        content: `Теперь, когда Sokuji запущен, давайте настроим его для первого использования:`,
        subsections: [
          {
            title: "Начальная настройка",
            content: `<ol>
              <li>Предоставьте разрешения на доступ к микрофону, когда macOS запросит</li>
              <li>Выберите предпочитаемый язык интерфейса</li>
              <li>Выберите поставщика ИИ (OpenAI, Google Gemini и т.д.)</li>
              <li>Введите API-ключ для выбранного поставщика</li>
              <li>Выберите исходный и целевой языки для перевода</li>
              <li>Выберите микрофон, а в блоке <strong>Речь</strong> выберите динамики или наушники как <strong>Устройство воспроизведения по умолчанию</strong>. Собственное виртуальное аудиоустройство Sokuji там не показывается: <strong>Перевод, который слышит собеседник</strong> отправляет в него ваш перевод, это виртуальный микрофон, который вы выбираете в приложении для встреч</li>
              <li>Протестируйте настройку микрофона и динамиков</li>
            </ol>`
          }
        ],
        success: "Поздравляем! Sokuji готов к использованию. Виртуальный аудиодрайвер установлен и доступен в вашем списке аудиоустройств. Нажмите кнопку микрофона, чтобы начать перевод в реальном времени."
      }
    ],
    troubleshooting: {
      title: "Устранение неполадок",
      issues: [
        {
          title: "Микрофон не работает",
          content: `Если Sokuji не может получить доступ к микрофону:
          <ol>
            <li>Откройте Системные настройки → Защита и безопасность → Конфиденциальность</li>
            <li>Выберите Микрофон в левой боковой панели</li>
            <li>Убедитесь, что Sokuji отмечен в списке</li>
            <li>Если его нет в списке, перезапустите Sokuji и предоставьте разрешение при запросе</li>
          </ol>`
        },
        {
          title: "Нет аудиовыхода",
          content: `Если вы не слышите переведенный звук:
          <ol>
            <li>Откройте Системные настройки → Звук → Выход</li>
            <li>Выберите предпочитаемое устройство вывода</li>
            <li>Проверьте, не отключена ли громкость вывода</li>
            <li>В блоке <strong>Речь</strong> в Sokuji проверьте устройство той строки, которую вы ожидаете услышать: <strong>Я тоже слышу</strong> для вашего собственного перевода (по умолчанию выключено), <strong>Перевод, который слышу я</strong> для перевода собеседника и <strong>Устройство воспроизведения по умолчанию</strong> для всего остального</li>
          </ol>`
        },
        {
          title: "Приложение вылетает на Mac с Intel или проблемы с производительностью",
          content: `Убедитесь, что вы скачали правильный установщик для вашего Mac:
          <ul>
            <li><strong>Apple Silicon</strong> (M1/M2/M3/M4): Используйте <code>Sokuji-x.x.x-arm64.pkg</code></li>
            <li><strong>Intel</strong>: Используйте <code>Sokuji-x.x.x-x64.pkg</code></li>
            <li>Проверьте тип чипа: меню Apple () → «Об этом Mac»</li>
            <li>Если использовали неправильный установщик, скачайте правильную версию со <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">страницы релизов</a></li>
            <li>Если вылеты продолжаются с правильным установщиком, проверьте Console.app для логов сбоев и сообщите о них на GitHub</li>
          </ul>`
        },
        {
          title: "Виртуальный аудиодрайвер не отображается",
          content: `Если вы не видите «SokujiVirtualAudio» в аудиоустройствах:
          <ol>
            <li>Сначала дайте Sokuji исправить это самому: если драйвер установлен, но macOS его не загрузила, Sokuji сообщает об этом при запуске и предлагает <strong>Исправить</strong>. Введите пароль администратора, и Sokuji перезапустит Core Audio, чтобы загрузить драйвер; перезапускать Sokuji не нужно</li>
            <li>Проверьте, установлен ли драйвер: <code>ls /Library/Audio/Plug-Ins/HAL/</code></li>
            <li>Если отсутствует, возможно, потребуется переустановить Sokuji</li>
            <li>Если присутствует, а «Исправить» не помогло, попробуйте перезапустить CoreAudio: <code>sudo killall coreaudiod</code>, или перезагрузите Mac</li>
            <li>Проверьте Системные настройки → Звук, чтобы увидеть, появилось ли устройство</li>
          </ol>`
        },
        {
          title: "Установка требует пароль администратора",
          content: `Это нормально и необходимо:
          <ul>
            <li>Установщик PKG должен установить системный аудиодрайвер</li>
            <li>Системные аудиодрайверы требуют прав администратора для установки</li>
            <li>Введите пароль при запросе — это безопасно и ожидаемо</li>
            <li>Если у вас нет прав администратора, обратитесь к системному администратору</li>
          </ul>`
        }
      ]
    },
  },
  de: {
    pageTitle: "macOS-Installationsanleitung",
    langLabel: "Sprache:",
    backLink: "← Zurueck zur Dokumentation",
    tocTitle: "Inhaltsverzeichnis",
    mobileJumpTo: "Zum Abschnitt springen...",
    overview: "Diese Anleitung fuehrt Sie durch die Installation von Sokuji auf macOS-Systemen. Sokuji ist als signierter und notarisierter .pkg-Installer verfuegbar, der einen virtuellen Audiotreiber fuer erweiterte Audioverarbeitungsfunktionen enthaelt.",
    requirements: {
      title: "Systemanforderungen",
      items: [
        "macOS 13 (Ventura) oder spaeter (seit v0.42.2; unter macOS 12 weiterhin v0.42.1 verwenden)",
        "Apple Silicon (M1/M2/M3/M4) oder Intel (x86_64) Prozessor",
        "Mindestens 4 GB RAM (8 GB empfohlen)",
        "200 MB verfuegbarer Festplattenspeicher",
        "Administratorrechte (fuer die Installation des virtuellen Audiotreibers erforderlich)",
        "Internetverbindung fuer KI-Uebersetzungsdienste",
        "Mikrofon und Lautsprecher/Kopfhoerer"
      ]
    },
    steps: [
      {
        title: "Schritt 1: Sokuji herunterladen",
        content: `Besuchen Sie die <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">offizielle GitHub-Releases-Seite</a>, um die neueste Version von Sokuji fuer macOS herunterzuladen.`,
        subsections: [
          {
            title: "PKG-Installer herunterladen",
            content: `<ul>
              <li><strong>Sokuji-x.x.x-arm64.pkg</strong> - Fuer Apple Silicon Macs (M1/M2/M3/M4)</li>
              <li><strong>Sokuji-x.x.x-x64.pkg</strong> - Fuer Intel Macs</li>
              <li>Jeder Installer enthaelt die Sokuji-App und den virtuellen Audiotreiber</li>
              <li>Die Dateigroesse betraegt etwa 150-200 MB</li>
            </ul>
            <p><strong>Tipp:</strong> Nicht sicher, welchen Sie herunterladen sollen? Klicken Sie auf das Apple-Menue () → "Ueber diesen Mac", um Ihren Chiptyp zu ueberpruefen. Wenn Sie "Apple M1/M2/M3/M4" sehen, waehlen Sie die arm64-Version. Wenn Sie "Intel" sehen, waehlen Sie die x64-Version.</p>`
          }
        ],
        info: "Seit Version 0.46.5 ist der PKG-Installer signiert und notarisiert, macOS öffnet ihn also ohne zusätzliche Schritte."
      },
      {
        title: "Schritt 2: PKG installieren",
        content: `Installieren Sie Sokuji und seinen virtuellen Audiotreiber.`,
        subsections: [
          {
            title: "Installationsprozess",
            content: `<ol>
              <li>Suchen Sie die heruntergeladene <code>.pkg</code>-Datei in Ihrem Downloads-Ordner (z.B. <code>Sokuji-x.x.x-arm64.pkg</code> oder <code>Sokuji-x.x.x-x64.pkg</code>)</li>
              <li>Doppelklicken Sie auf die PKG-Datei, um die Installation zu starten</li>
              <li>Der macOS-Installer wird geoeffnet - klicken Sie auf "Fortfahren"</li>
              <li>Ueberpruefen Sie die Installationsdetails und klicken Sie auf "Installieren"</li>
              <li>Geben Sie Ihr Administratorpasswort ein, wenn Sie dazu aufgefordert werden (fuer die Treiberinstallation erforderlich)</li>
              <li>Warten Sie, bis die Installation abgeschlossen ist (normalerweise 1-2 Minuten)</li>
              <li>Klicken Sie auf "Schliessen", wenn die Installation abgeschlossen ist</li>
            </ol>`
          },
          {
            title: "Was installiert wird",
            content: `<ul>
              <li><strong>Sokuji.app</strong> - Wird in /Applications installiert</li>
              <li><strong>SokujiVirtualAudio.driver</strong> - Virtueller Audiotreiber, installiert in /Library/Audio/Plug-Ins/HAL</li>
              <li>Der Installer startet CoreAudio automatisch neu, um den Treiber zu laden</li>
            </ul>`
          }
        ],
        info: "Das Administratorpasswort ist erforderlich, da der Installer einen Audiotreiber auf Systemebene installieren muss. Der virtuelle Audiotreiber ermoeglicht erweiterte Audioverarbeitungsfunktionen."
      },
      {
        title: "Schritt 3: Erster Start",
        content: `Öffnen Sie Sokuji aus dem Ordner „Programme“. Zusätzliche Sicherheitsschritte sind nicht nötig.`
      },
      {
        title: "Schritt 4: Sokuji konfigurieren",
        content: `Nachdem Sokuji laeuft, konfigurieren wir es fuer die erste Verwendung:`,
        subsections: [
          {
            title: "Ersteinrichtung",
            content: `<ol>
              <li>Erteilen Sie Mikrofonberechtigungen, wenn macOS Sie dazu auffordert</li>
              <li>Waehlen Sie Ihre bevorzugte Oberflaechensprache</li>
              <li>Waehlen Sie Ihren KI-Anbieter (OpenAI, Google Gemini usw.)</li>
              <li>Geben Sie Ihren API-Schluessel fuer den ausgewaehlten Anbieter ein</li>
              <li>Waehlen Sie Quell- und Zielsprache fuer die Uebersetzung</li>
              <li>Wählen Sie Ihr Mikrofon, und wählen Sie im Block <strong>Sprache</strong> Ihre Lautsprecher oder Kopfhörer als <strong>Standard-Wiedergabegerät</strong>. Sokuji führt dort sein eigenes virtuelles Audiogerät nicht auf: <strong>Übersetzung, die das Gegenüber hört</strong> schickt Ihre Übersetzung hinein, es ist das virtuelle Mikrofon, das Sie in Ihrer Meeting-App wählen</li>
              <li>Testen Sie Ihre Mikrofon- und Lautsprechereinrichtung</li>
            </ol>`
          }
        ],
        success: "Herzlichen Glueckwunsch! Sokuji ist jetzt einsatzbereit. Der virtuelle Audiotreiber wurde installiert und ist in Ihrer Audiogeraete-Liste verfuegbar. Klicken Sie auf die Mikrofon-Schaltflaeche, um die Echtzeit-Uebersetzung zu starten."
      }
    ],
    troubleshooting: {
      title: "Fehlerbehebung",
      issues: [
        {
          title: "Mikrofon funktioniert nicht",
          content: `Wenn Sokuji nicht auf Ihr Mikrofon zugreifen kann:
          <ol>
            <li>Oeffnen Sie Systemeinstellungen → Sicherheit & Datenschutz → Datenschutz</li>
            <li>Waehlen Sie Mikrofon in der linken Seitenleiste</li>
            <li>Stellen Sie sicher, dass Sokuji in der Liste aktiviert ist</li>
            <li>Wenn nicht aufgelistet, starten Sie Sokuji neu und erteilen Sie die Berechtigung bei Aufforderung</li>
          </ol>`
        },
        {
          title: "Keine Audioausgabe",
          content: `Wenn Sie den uebersetzten Ton nicht hoeren koennen:
          <ol>
            <li>Oeffnen Sie Systemeinstellungen → Ton → Ausgabe</li>
            <li>Waehlen Sie Ihr bevorzugtes Ausgabegeraet</li>
            <li>Pruefen Sie, ob die Ausgabelautstaerke nicht stummgeschaltet ist</li>
            <li>Prüfen Sie im Block <strong>Sprache</strong> in Sokuji das Gerät der Zeile, die Sie hören möchten: <strong>Ich höre es auch</strong> für Ihre eigene Übersetzung (standardmäßig aus), <strong>Übersetzung, die ich höre</strong> für die des Gegenübers und <strong>Standard-Wiedergabegerät</strong> für alles andere</li>
          </ol>`
        },
        {
          title: "App stuerzt auf Intel Macs ab oder Leistungsprobleme",
          content: `Stellen Sie sicher, dass Sie den richtigen Installer fuer Ihren Mac heruntergeladen haben:
          <ul>
            <li><strong>Apple Silicon</strong> (M1/M2/M3/M4): Verwenden Sie <code>Sokuji-x.x.x-arm64.pkg</code></li>
            <li><strong>Intel</strong>: Verwenden Sie <code>Sokuji-x.x.x-x64.pkg</code></li>
            <li>Pruefen Sie Ihren Chiptyp: Apple-Menue () → "Ueber diesen Mac"</li>
            <li>Wenn Sie den falschen Installer verwendet haben, laden Sie die richtige Version von der <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">Releases-Seite</a> herunter</li>
            <li>Wenn Abstuerze mit dem richtigen Installer anhalten, ueberpruefen Sie Console.app auf Absturzprotokolle und melden Sie diese auf GitHub</li>
          </ul>`
        },
        {
          title: "Virtueller Audiotreiber wird nicht angezeigt",
          content: `Wenn Sie "SokujiVirtualAudio" nicht in Ihren Audiogeraeten sehen:
          <ol>
            <li>Lassen Sie es zuerst von Sokuji reparieren: Ist der Treiber installiert, aber von macOS nicht geladen, meldet Sokuji das beim Start und bietet <strong>Reparieren</strong> an. Geben Sie Ihr Administratorpasswort ein; Sokuji startet Core Audio neu, um den Treiber zu laden, ohne dass Sokuji neu gestartet werden muss</li>
            <li>Pruefen Sie, ob der Treiber installiert ist: <code>ls /Library/Audio/Plug-Ins/HAL/</code></li>
            <li>Wenn nicht vorhanden, muessen Sie Sokuji moeglicherweise neu installieren</li>
            <li>Wenn vorhanden und Reparieren nicht geholfen hat, versuchen Sie CoreAudio neu zu starten: <code>sudo killall coreaudiod</code>, oder starten Sie den Mac neu</li>
            <li>Pruefen Sie Systemeinstellungen → Ton, ob das Geraet erscheint</li>
          </ol>`
        },
        {
          title: "Installation erfordert Administratorpasswort",
          content: `Dies ist normal und erforderlich:
          <ul>
            <li>Der PKG-Installer muss einen Audiotreiber auf Systemebene installieren</li>
            <li>System-Audiotreiber erfordern Administratorrechte fuer die Installation</li>
            <li>Geben Sie Ihr Passwort ein, wenn Sie dazu aufgefordert werden - dies ist sicher und erwartet</li>
            <li>Wenn Sie keinen Administratorzugang haben, wenden Sie sich an Ihren Systemadministrator</li>
          </ul>`
        }
      ]
    },
  },
  uk: {
    pageTitle: "Посібник зі встановлення на macOS",
    langLabel: "Мова:",
    backLink: "← Повернутися до документації",
    tocTitle: "Зміст",
    mobileJumpTo: "Перейти до розділу...",
    overview: "Цей посібник допоможе вам встановити Sokuji на системи macOS. Sokuji доступний як підписаний і нотаризований інсталятор .pkg, який включає віртуальний аудіодрайвер для розширених можливостей обробки звуку.",
    requirements: {
      title: "Системні вимоги",
      items: [
        "macOS 13 (Ventura) або новіша (починаючи з v0.42.2; на macOS 12 продовжуйте користуватися v0.42.1)",
        "Процесор Apple Silicon (M1/M2/M3/M4) або Intel (x86_64)",
        "Мінімум 4 ГБ оперативної памʼяті (рекомендовано 8 ГБ)",
        "200 МБ вільного дискового простору",
        "Права адміністратора (необхідні для встановлення віртуального аудіодрайвера)",
        "Підключення до інтернету для служб перекладу з ШІ",
        "Мікрофон та динаміки/навушники"
      ]
    },
    steps: [
      {
        title: "Крок 1: Завантажити Sokuji",
        content: `Відвідайте <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">офіційну сторінку релізів на GitHub</a>, щоб завантажити останню версію Sokuji для macOS.`,
        subsections: [
          {
            title: "Завантажити інсталятор PKG",
            content: `<ul>
              <li><strong>Sokuji-x.x.x-arm64.pkg</strong> - Для Mac з Apple Silicon (M1/M2/M3/M4)</li>
              <li><strong>Sokuji-x.x.x-x64.pkg</strong> - Для Mac з Intel</li>
              <li>Кожен інсталятор включає програму Sokuji та віртуальний аудіодрайвер</li>
              <li>Розмір файлу приблизно 150-200 МБ</li>
            </ul>
            <p><strong>Порада:</strong> Не впевнені, який завантажити? Натисніть меню Apple () → "Про цей Mac", щоб перевірити тип чіпа. Якщо бачите "Apple M1/M2/M3/M4", оберіть версію arm64. Якщо бачите "Intel", оберіть версію x64.</p>`
          }
        ],
        info: "Починаючи з версії 0.46.5 інсталятор PKG підписаний і нотаризований, тому macOS відкриває його без додаткових кроків."
      },
      {
        title: "Крок 2: Встановити PKG",
        content: `Встановіть Sokuji та його віртуальний аудіодрайвер.`,
        subsections: [
          {
            title: "Процес встановлення",
            content: `<ol>
              <li>Знайдіть завантажений файл <code>.pkg</code> у папці Завантаження (наприклад, <code>Sokuji-x.x.x-arm64.pkg</code> або <code>Sokuji-x.x.x-x64.pkg</code>)</li>
              <li>Двічі клацніть на файл PKG, щоб почати встановлення</li>
              <li>Відкриється інсталятор macOS — натисніть "Продовжити" для продовження</li>
              <li>Перегляньте деталі встановлення та натисніть "Встановити"</li>
              <li>Введіть пароль адміністратора при запиті (необхідний для встановлення драйвера)</li>
              <li>Зачекайте завершення встановлення (зазвичай 1-2 хвилини)</li>
              <li>Натисніть "Закрити" після завершення встановлення</li>
            </ol>`
          },
          {
            title: "Що встановлюється",
            content: `<ul>
              <li><strong>Sokuji.app</strong> - Встановлюється в /Applications</li>
              <li><strong>SokujiVirtualAudio.driver</strong> - Віртуальний аудіодрайвер, що встановлюється в /Library/Audio/Plug-Ins/HAL</li>
              <li>Інсталятор автоматично перезапустить CoreAudio для завантаження драйвера</li>
            </ul>`
          }
        ],
        info: "Пароль адміністратора необхідний, оскільки інсталятору потрібно встановити системний аудіодрайвер. Віртуальний аудіодрайвер забезпечує розширені функції обробки звуку."
      },
      {
        title: "Крок 3: Перший запуск",
        content: `Відкрийте Sokuji з теки «Програми». Додаткові кроки безпеки не потрібні.`
      },
      {
        title: "Крок 4: Налаштувати Sokuji",
        content: `Тепер, коли Sokuji запущено, давайте налаштуємо його для першого використання:`,
        subsections: [
          {
            title: "Початкове налаштування",
            content: `<ol>
              <li>Надайте дозвіл на доступ до мікрофона, коли macOS запитає</li>
              <li>Оберіть бажану мову інтерфейсу</li>
              <li>Оберіть постачальника ШІ (OpenAI, Google Gemini тощо)</li>
              <li>Введіть API-ключ для обраного постачальника</li>
              <li>Оберіть мову джерела та цільову мову для перекладу</li>
              <li>Оберіть мікрофон, а в блоці <strong>Мовлення</strong> оберіть динаміки або навушники як <strong>Пристрій відтворення за замовчуванням</strong>. Власний віртуальний аудіопристрій Sokuji там не показується: <strong>Переклад, який чує співрозмовник</strong> надсилає до нього ваш переклад, це віртуальний мікрофон, який ви обираєте в програмі для зустрічей</li>
              <li>Протестуйте налаштування мікрофона та динаміків</li>
            </ol>`
          }
        ],
        success: "Вітаємо! Sokuji готовий до використання. Віртуальний аудіодрайвер встановлено та доступний у вашому списку аудіопристроїв. Натисніть кнопку мікрофона, щоб розпочати переклад у реальному часі."
      }
    ],
    troubleshooting: {
      title: "Усунення несправностей",
      issues: [
        {
          title: "Мікрофон не працює",
          content: `Якщо Sokuji не може отримати доступ до мікрофона:
          <ol>
            <li>Відкрийте Системні налаштування → Безпека та конфіденційність → Конфіденційність</li>
            <li>Оберіть Мікрофон у лівій бічній панелі</li>
            <li>Переконайтеся, що Sokuji позначено у списку</li>
            <li>Якщо його немає у списку, перезапустіть Sokuji та надайте дозвіл при запиті</li>
          </ol>`
        },
        {
          title: "Немає аудіовиходу",
          content: `Якщо ви не чуєте перекладений звук:
          <ol>
            <li>Відкрийте Системні налаштування → Звук → Вихід</li>
            <li>Оберіть бажаний пристрій виводу</li>
            <li>Перевірте, чи не вимкнено гучність виводу</li>
            <li>У блоці <strong>Мовлення</strong> в Sokuji перевірте пристрій того рядка, який ви очікуєте почути: <strong>Я теж чую</strong> для вашого власного перекладу (типово вимкнено), <strong>Переклад, який чую я</strong> для перекладу співрозмовника і <strong>Пристрій відтворення за замовчуванням</strong> для всього іншого</li>
          </ol>`
        },
        {
          title: "Програма вилітає на Mac з Intel або проблеми з продуктивністю",
          content: `Переконайтеся, що ви завантажили правильний інсталятор для вашого Mac:
          <ul>
            <li><strong>Apple Silicon</strong> (M1/M2/M3/M4): Використовуйте <code>Sokuji-x.x.x-arm64.pkg</code></li>
            <li><strong>Intel</strong>: Використовуйте <code>Sokuji-x.x.x-x64.pkg</code></li>
            <li>Перевірте тип чіпа: меню Apple () → "Про цей Mac"</li>
            <li>Якщо використали неправильний інсталятор, завантажте правильну версію зі <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">сторінки релізів</a></li>
            <li>Якщо вильоти продовжуються з правильним інсталятором, перевірте Console.app для логів збоїв та повідомте про них на GitHub</li>
          </ul>`
        },
        {
          title: "Віртуальний аудіодрайвер не відображається",
          content: `Якщо ви не бачите "SokujiVirtualAudio" в аудіопристроях:
          <ol>
            <li>Спершу дайте Sokuji виправити це: якщо драйвер встановлено, але macOS його не завантажила, Sokuji повідомляє про це під час запуску й пропонує <strong>Виправити</strong>. Введіть пароль адміністратора — Sokuji перезапустить Core Audio, щоб завантажити драйвер; перезапускати Sokuji не потрібно</li>
            <li>Перевірте, чи встановлено драйвер: <code>ls /Library/Audio/Plug-Ins/HAL/</code></li>
            <li>Якщо відсутній, можливо, потрібно перевстановити Sokuji</li>
            <li>Якщо присутній, а виправлення не допомогло, спробуйте перезапустити CoreAudio: <code>sudo killall coreaudiod</code>, або перезавантажте Mac</li>
            <li>Перевірте Системні налаштування → Звук, чи зʼявився пристрій</li>
          </ol>`
        },
        {
          title: "Встановлення вимагає пароль адміністратора",
          content: `Це нормально та необхідно:
          <ul>
            <li>Інсталятор PKG повинен встановити системний аудіодрайвер</li>
            <li>Системні аудіодрайвери вимагають прав адміністратора для встановлення</li>
            <li>Введіть пароль при запиті — це безпечно та очікувано</li>
            <li>Якщо у вас немає прав адміністратора, звʼяжіться з системним адміністратором</li>
          </ul>`
        }
      ]
    },
  },
  it: {
    pageTitle: "Guida all'installazione su macOS",
    langLabel: "Lingua:",
    backLink: "← Torna alla documentazione",
    tocTitle: "Indice",
    mobileJumpTo: "Vai alla sezione...",
    overview: "Questa guida vi accompagnera nell'installazione di Sokuji sui sistemi macOS. Sokuji e disponibile come installer .pkg firmato e notarizzato che include un driver audio virtuale per capacita avanzate di elaborazione audio.",
    requirements: {
      title: "Requisiti di sistema",
      items: [
        "macOS 13 (Ventura) o successivo (da v0.42.2; su macOS 12 continuate a usare v0.42.1)",
        "Processore Apple Silicon (M1/M2/M3/M4) o Intel (x86_64)",
        "4 GB di RAM minimo (8 GB consigliati)",
        "200 MB di spazio su disco disponibile",
        "Privilegi di amministratore (necessari per l'installazione del driver audio virtuale)",
        "Connessione Internet per i servizi di traduzione con IA",
        "Microfono e altoparlanti/cuffie"
      ]
    },
    steps: [
      {
        title: "Passaggio 1: Scaricare Sokuji",
        content: `Visitate la <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">pagina ufficiale delle release su GitHub</a> per scaricare l'ultima versione di Sokuji per macOS.`,
        subsections: [
          {
            title: "Scaricare l'installer PKG",
            content: `<ul>
              <li><strong>Sokuji-x.x.x-arm64.pkg</strong> - Per Mac con Apple Silicon (M1/M2/M3/M4)</li>
              <li><strong>Sokuji-x.x.x-x64.pkg</strong> - Per Mac con Intel</li>
              <li>Ogni installer include l'applicazione Sokuji e il driver audio virtuale</li>
              <li>La dimensione del file e di circa 150-200 MB</li>
            </ul>
            <p><strong>Suggerimento:</strong> Non siete sicuri di quale scaricare? Cliccate sul menu Apple () → "Informazioni su questo Mac" per verificare il tipo di chip. Se vedete "Apple M1/M2/M3/M4", scegliete la versione arm64. Se vedete "Intel", scegliete la versione x64.</p>`
          }
        ],
        info: "Dalla versione 0.46.5 l'installer PKG è firmato e notarizzato, quindi macOS lo apre senza passaggi aggiuntivi."
      },
      {
        title: "Passaggio 2: Installare il PKG",
        content: `Installate Sokuji e il suo driver audio virtuale.`,
        subsections: [
          {
            title: "Processo di installazione",
            content: `<ol>
              <li>Individuate il file <code>.pkg</code> scaricato nella cartella Download (es., <code>Sokuji-x.x.x-arm64.pkg</code> o <code>Sokuji-x.x.x-x64.pkg</code>)</li>
              <li>Fate doppio clic sul file PKG per avviare l'installazione</li>
              <li>L'installer di macOS si aprira - cliccate su "Continua" per procedere</li>
              <li>Esaminate i dettagli dell'installazione e cliccate su "Installa"</li>
              <li>Inserite la password di amministratore quando richiesto (necessaria per l'installazione del driver)</li>
              <li>Attendete il completamento dell'installazione (di solito 1-2 minuti)</li>
              <li>Cliccate su "Chiudi" al termine dell'installazione</li>
            </ol>`
          },
          {
            title: "Cosa viene installato",
            content: `<ul>
              <li><strong>Sokuji.app</strong> - Installato in /Applications</li>
              <li><strong>SokujiVirtualAudio.driver</strong> - Driver audio virtuale installato in /Library/Audio/Plug-Ins/HAL</li>
              <li>L'installer riavviera automaticamente CoreAudio per caricare il driver</li>
            </ul>`
          }
        ],
        info: "La password di amministratore e necessaria perche l'installer deve installare un driver audio a livello di sistema. Il driver audio virtuale abilita funzionalita avanzate di elaborazione audio."
      },
      {
        title: "Passaggio 3: Primo avvio",
        content: `Aprite Sokuji dalla cartella Applicazioni. Non servono passaggi di sicurezza aggiuntivi.`
      },
      {
        title: "Passaggio 4: Configurare Sokuji",
        content: `Ora che Sokuji e in esecuzione, configuriamolo per il primo utilizzo:`,
        subsections: [
          {
            title: "Configurazione iniziale",
            content: `<ol>
              <li>Concedete i permessi del microfono quando macOS lo richiede</li>
              <li>Selezionate la vostra lingua dell'interfaccia preferita</li>
              <li>Scegliete il vostro fornitore di IA (OpenAI, Google Gemini, ecc.)</li>
              <li>Inserite la vostra chiave API per il fornitore selezionato</li>
              <li>Selezionate le lingue di origine e di destinazione per la traduzione</li>
              <li>Selezionate il microfono e, nel blocco <strong>Voce</strong>, scegliete i vostri altoparlanti o le cuffie come <strong>Dispositivo di riproduzione predefinito</strong>. Sokuji non elenca lì il proprio dispositivo audio virtuale: <strong>Traduzione che sente l'altra parte</strong> vi invia la vostra traduzione, è il microfono virtuale che scegliete nell'app per riunioni</li>
              <li>Testate la configurazione del microfono e degli altoparlanti</li>
            </ol>`
          }
        ],
        success: "Congratulazioni! Sokuji e ora pronto per l'uso. Il driver audio virtuale e stato installato ed e disponibile nel vostro elenco dei dispositivi audio. Cliccate sul pulsante del microfono per avviare la traduzione in tempo reale."
      }
    ],
    troubleshooting: {
      title: "Risoluzione dei problemi",
      issues: [
        {
          title: "Il microfono non funziona",
          content: `Se Sokuji non riesce ad accedere al microfono:
          <ol>
            <li>Aprite Preferenze di Sistema → Sicurezza e Privacy → Privacy</li>
            <li>Selezionate Microfono dalla barra laterale sinistra</li>
            <li>Assicuratevi che Sokuji sia selezionato nell'elenco</li>
            <li>Se non e nell'elenco, riavviate Sokuji e concedete il permesso quando richiesto</li>
          </ol>`
        },
        {
          title: "Nessuna uscita audio",
          content: `Se non riuscite a sentire l'audio tradotto:
          <ol>
            <li>Aprite Preferenze di Sistema → Suono → Uscita</li>
            <li>Selezionate il dispositivo di uscita preferito</li>
            <li>Verificate che il volume di uscita non sia disattivato</li>
            <li>Nel blocco <strong>Voce</strong> di Sokuji, controllate il dispositivo della riga che vi aspettate di sentire: <strong>Lo sento anche io</strong> per la vostra traduzione (disattivato per impostazione predefinita), <strong>Traduzione che sento io</strong> per quella dell'altra parte e <strong>Dispositivo di riproduzione predefinito</strong> per tutto il resto</li>
          </ol>`
        },
        {
          title: "L'app si blocca su Mac Intel o problemi di prestazioni",
          content: `Assicuratevi di aver scaricato l'installer corretto per il vostro Mac:
          <ul>
            <li><strong>Apple Silicon</strong> (M1/M2/M3/M4): Usate <code>Sokuji-x.x.x-arm64.pkg</code></li>
            <li><strong>Intel</strong>: Usate <code>Sokuji-x.x.x-x64.pkg</code></li>
            <li>Verificate il tipo di chip: menu Apple () → "Informazioni su questo Mac"</li>
            <li>Se avete usato l'installer sbagliato, scaricate la versione corretta dalla <a href="https://github.com/kizuna-ai-lab/sokuji/releases/latest" target="_blank" rel="noopener noreferrer">pagina delle release</a></li>
            <li>Se i crash persistono con l'installer corretto, controllate Console.app per i log dei crash e segnalateli su GitHub</li>
          </ul>`
        },
        {
          title: "Il driver audio virtuale non viene visualizzato",
          content: `Se non vedete "SokujiVirtualAudio" nei vostri dispositivi audio:
          <ol>
            <li>Lasciate prima che Sokuji lo ripari: quando il driver è installato ma macOS non lo ha caricato, Sokuji lo segnala all'avvio e propone <strong>Ripara</strong>. Inserite la password di amministratore e Sokuji riavvia Core Audio per caricare il driver, senza bisogno di riavviare Sokuji</li>
            <li>Verificate se il driver e installato: <code>ls /Library/Audio/Plug-Ins/HAL/</code></li>
            <li>Se non presente, potrebbe essere necessario reinstallare Sokuji</li>
            <li>Se presente e Ripara non ha aiutato, provate a riavviare CoreAudio: <code>sudo killall coreaudiod</code>, oppure riavviate il Mac</li>
            <li>Controllate Impostazioni di Sistema → Suono per vedere se il dispositivo appare</li>
          </ol>`
        },
        {
          title: "L'installazione richiede la password di amministratore",
          content: `Questo e normale e necessario:
          <ul>
            <li>L'installer PKG deve installare un driver audio a livello di sistema</li>
            <li>I driver audio di sistema richiedono privilegi di amministratore per l'installazione</li>
            <li>Inserite la password quando richiesto - e sicuro e previsto</li>
            <li>Se non avete accesso come amministratore, contattate il vostro amministratore di sistema</li>
          </ul>`
        }
      ]
    },
  }
};

export function MacOSInstall() {
  const { locale } = useI18n();
  const lp = useLocalizedPath();
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  // Get current language data
  const currentLang = locale.startsWith('zh') ? 'zh' :
                      locale.startsWith('ja') ? 'ja' :
                      locale.startsWith('ko') ? 'ko' :
                      locale.startsWith('es') ? 'es' :
                      locale.startsWith('fr') ? 'fr' :
                      locale.startsWith('ar') ? 'ar' :
                      locale.startsWith('pt') ? 'pt' :
                      locale.startsWith('ru') ? 'ru' :
                      locale.startsWith('de') ? 'de' :
                      locale.startsWith('uk') ? 'uk' :
                      locale.startsWith('it') ? 'it' : 'en';
  const t = translations[currentLang] || translations.en;

  // Handle clicks on content that may contain images
  const handleContentClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === 'IMG') {
      const src = (target as HTMLImageElement).src;
      setLightboxImage(src);
    }
  }, []);

  return (
    <div className="docs-content install-page">
      {/* Back link */}
      <Link to={lp('/docs')} className="tutorial-page__back-link">
        <ArrowLeft size={16} />
        {t.backLink}
      </Link>

      <h1>{t.pageTitle}</h1>

      <p className="install-page__overview">{t.overview}</p>

      {/* Download button */}
      <a
        href="https://github.com/kizuna-ai-lab/sokuji/releases/latest"
        target="_blank"
        rel="noopener noreferrer"
        className="install-page__download-btn"
      >
        <Download size={20} />
        {currentLang === 'zh' ? '下载 macOS 版本' :
         currentLang === 'ja' ? 'macOS版をダウンロード' :
         currentLang === 'ko' ? 'macOS용 다운로드' :
         currentLang === 'es' ? 'Descargar para macOS' :
         currentLang === 'fr' ? 'Telecharger pour macOS' :
         currentLang === 'ar' ? 'تحميل لنظام macOS' :
         currentLang === 'pt' ? 'Baixar para macOS' :
         currentLang === 'ru' ? 'Скачать для macOS' :
         currentLang === 'de' ? 'Fuer macOS herunterladen' :
         currentLang === 'uk' ? 'Завантажити для macOS' :
         currentLang === 'it' ? 'Scarica per macOS' :
         'Download for macOS'}
        <ExternalLink size={16} />
      </a>

      {/* System Requirements */}
      <div className="install-page__requirements">
        <h3>{t.requirements.title}</h3>
        <ul>
          {t.requirements.items.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      </div>

      {/* Installation Steps */}
      <h2>{currentLang === 'zh' ? '安装步骤' :
           currentLang === 'ja' ? 'インストール手順' :
           currentLang === 'ko' ? '설치 단계' :
           currentLang === 'es' ? 'Pasos de instalacion' :
           currentLang === 'fr' ? "Etapes d'installation" :
           currentLang === 'ar' ? 'خطوات التثبيت' :
           currentLang === 'pt' ? 'Etapas de instalacao' :
           currentLang === 'ru' ? 'Шаги установки' :
           currentLang === 'de' ? 'Installationsschritte' :
           currentLang === 'uk' ? 'Кроки встановлення' :
           currentLang === 'it' ? "Passaggi dell'installazione" :
           'Installation Steps'}</h2>

      {t.steps.map((step, stepIndex) => (
        <div key={stepIndex} className="install-page__step" onClick={handleContentClick}>
          <h3>{step.title}</h3>
          <div dangerouslySetInnerHTML={{ __html: step.content }} />

          {step.subsections?.map((sub, subIndex) => (
            <div key={subIndex}>
              <h4>{sub.title}</h4>
              <div dangerouslySetInnerHTML={{ __html: sub.content }} />
            </div>
          ))}

          {step.warning && (
            <div className="install-page__warning">{step.warning}</div>
          )}

          {step.info && (
            <div className="install-page__info">{step.info}</div>
          )}

          {step.success && (
            <div className="install-page__success">{step.success}</div>
          )}
        </div>
      ))}

      {/* Troubleshooting */}
      <div className="install-page__troubleshooting">
        <h2>{t.troubleshooting.title}</h2>

        {t.troubleshooting.issues.map((issue, index) => (
          <div key={index} className="install-page__issue">
            <h3>{issue.title}</h3>
            <div dangerouslySetInnerHTML={{ __html: issue.content }} />
          </div>
        ))}
      </div>

      {/* Lightbox for images */}
      <Lightbox
        src={lightboxImage || ''}
        alt="Screenshot"
        isOpen={!!lightboxImage}
        onClose={() => setLightboxImage(null)}
      />
    </div>
  );
}
